import React, { Component } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  company,
  loading,
  NavbarWarehouse,
  alert,
  GET,
  POST,
  t,
} from "../../components/CustomComponent.js";
import { canAny } from "../../utils/permission";
import { matchColumnsByHeader } from "../../utils/headerMatch";
// หน้าตรวจข้อมูลก่อนนำเข้า ใช้ตัวเดียวกับปุ่มนำเข้าในเมนูสต็อกสินค้า
import ImportPreviewModal, { importErrorText, stockImportSummaryText } from "../../components/ImportPreviewModal.js";

import locationTemplateFile from "../../assets/template/12_Location_Template.xlsx";
import warehouseTemplateFile from "../../assets/template/11_Warehouse_Template.xlsx";
import uomTemplateFile from "../../assets/template/07_UnitOfMeasure_Template.xlsx";
import categoryTemplateFile from "../../assets/template/08_Category_Template.xlsx";
import materialTypeTemplateFile from "../../assets/template/04_Material_Type_Template.xlsx";
import materialGroupTemplateFile from "../../assets/template/05.1_Material_Group_Template.xlsx";
import brandTemplateFile from "../../assets/template/05.2_Brand_Template.xlsx";
import partTemplateFile from "../../assets/template/09_Item_Master_Template.xlsx";
import customerTemplateFile from "../../assets/template/01_Customer_Template.xlsx";
import wbsTemplateFile from "../../assets/template/13_wbs_Template.xlsx";
import projectTemplateFile from "../../assets/template/16_project_Template.xlsx";
import inventoryTemplateFile from "../../assets/template/14_Inventory_Template.xlsx";
import technicianTemplateFile from "../../assets/template/15_Technician_Template.xlsx";
import assetTemplateFile from "../../assets/template/10_Asset_Template.xlsx";

// สต็อกใช้ตัวอ่านไฟล์/ตัวนำเข้าชุดเดียวกับหน้า Stock (นำเข้าแบบ full-replace ผ่าน batch)
import {
  STOCK_IMPORT_COLUMNS,
  STOCK_IMPORT_SHEET,
  STOCK_IMPORT_HEADER_ROWS,
  parseStockWorkbook,
  runStockImport,
} from "../../utils/stockImport.js";
import { formatNumber } from "../../utils/formatting";

/**
 * Column definitions for the Location data set.
 * `type`:
 *   auto   -> running number generated by the system (not validated / not imported)
 *   text   -> free text
 *   enum   -> must match one of `options` (matched by english key or thai label)
 *   number -> decimal number (optional)
 *   status -> เปิดใช้งาน / ปิดใช้งาน  ->  boolean
 */
const LOCATION_COLUMNS = [
  { key: "no", th: "ลำดับที่", en: "No.", type: "auto" },
  { key: "location_code", th: "รหัสตำแหน่ง", en: "Location Code", type: "text", required: true, unique: true },
  { key: "name", th: "ชื่อตำแหน่ง", en: "Location Name", type: "text", required: true },
  { key: "location_type", th: "ประเภทตำแหน่ง", en: "Location Type", type: "enum", required: true, options: ["zone", "row", "rack", "room", "other"] },
  { key: "address", th: "ที่อยู่ / รายละเอียด", en: "Address / Detail", type: "text" },
  { key: "latitude", th: "ละติจูด", en: "Latitude", type: "number" },
  { key: "longitude", th: "ลองจิจูด", en: "Longitude", type: "number" },
  { key: "description", th: "คำอธิบาย", en: "Description", type: "text" },
  { key: "is_active", th: "สถานะ", en: "Status", type: "status", required: true },
];

/**
 * Column definitions for the Warehouse data set (Template No. 11).
 * `branch` is checked against Master Data > Branches (name, case/space-insensitive, active only).
 */
const WAREHOUSE_COLUMNS = [
  { key: "no", th: "ลำดับที่", en: "No.", type: "auto" },
  { key: "branch", th: "สาขา", en: "Branch", type: "branch", required: true },
  { key: "warehouse_code", th: "รหัสคลังสินค้า", en: "Warehouse Code", type: "text", required: true, unique: true },
  { key: "name", th: "ชื่อคลังสินค้า", en: "Warehouse Name", type: "text", required: true },
  { key: "description", th: "คำอธิบาย", en: "Description", type: "text" },
  { key: "storage_locations", th: "พื้นที่เก็บ", en: "Storage Locations", type: "list" },
  { key: "is_active", th: "สถานะ", en: "Status", type: "status", required: true },
];

/**
 * Column definitions for the Unit of Measure data set (Template No. 07).
 * No lookup/enum columns — status accepts Active/Inactive (เปิดใช้งาน/ปิดใช้งาน).
 */
const UOM_COLUMNS = [
  { key: "no", th: "ลำดับที่", en: "Row No.", type: "auto" },
  { key: "uom_code", th: "รหัสหน่วยนับ", en: "Unit Code", type: "text", required: true, unique: true },
  { key: "name", th: "ชื่อหน่วยนับ", en: "Unit Name", type: "text", required: true },
  { key: "symbol", th: "สัญลักษณ์", en: "Symbol", type: "text" },
  { key: "description", th: "คำอธิบาย", en: "Description", type: "text" },
  { key: "is_active", th: "สถานะ", en: "Status", type: "status", required: true },
];

/**
 * Column definitions for the Category data set (Template No. 08).
 * Column headers are on row 8 (row 7 holds group titles) -> data starts at row 9.
 * Part Control columns (E-H) are Yes/No flags mapped into a `field_control` array.
 */
const CATEGORY_COLUMNS = [
  { key: "no", th: "ลำดับที่", en: "Row No.", type: "auto" },
  { key: "category_code", th: "รหัสหมวดหมู่", en: "Category Code", type: "text", required: true, unique: true },
  { key: "name", th: "ชื่อหมวดหมู่", en: "Category Name", type: "text", required: true },
  { key: "description", th: "คำอธิบาย", en: "Description", type: "text" },
  { key: "warehouse_control", th: "Warehouse", en: "Warehouse Control", type: "bool" },
  { key: "location_control", th: "Location", en: "Location Control", type: "bool" },
  { key: "batch_control", th: "Batch Number", en: "Batch Number Control", type: "bool" },
  { key: "date_control", th: "Date", en: "Date Control", type: "bool" },
  { key: "is_active", th: "สถานะ", en: "Status", type: "status", required: true },
];

/**
 * Column definitions for the Material Type data set (Template No. 04) — table `brands`.
 * UI name = "Material Type" (ประเภทวัสดุ); payload keys ยังเป็น brand_* ตาม API เดิม.
 * ลำดับชั้น (parent / level) ไม่อยู่ใน template — จัดการผ่านหน้า Material Type เท่านั้น
 * และ import จะไม่แตะ parent/level ของรายการที่มีอยู่แล้ว.
 */
const BRAND_COLUMNS = [
  { key: "no", th: "ลำดับที่", en: "Row No.", type: "auto" },
  { key: "brand_code", th: "รหัสประเภทวัสดุ", en: "Material Type Code", type: "text", required: true, unique: true },
  { key: "name", th: "ชื่อประเภทวัสดุ", en: "Material Type Name", type: "text", required: true },
  { key: "description", th: "คำอธิบาย", en: "Description", type: "text" },
  { key: "is_active", th: "สถานะ", en: "Status", type: "status", required: true },
];

/**
 * Column definitions for the Material Group data set (Template No. 05.1) — table `models`.
 * UI name = "Material Group" (กลุ่มวัสดุ); payload keys ยังเป็น model_* ตาม API เดิม.
 * `brand_code` is a Lookup against the company's Material Types (resolved to brand_id on save).
 * `brand_name` is an auto VLOOKUP column -> ignored on import (filled on export).
 */
const MODEL_COLUMNS = [
  { key: "no", th: "ลำดับที่", en: "Row No.", type: "auto" },
  { key: "model_code", th: "รหัสกลุ่มวัสดุ", en: "Material Group Code", type: "text", required: true, unique: true },
  { key: "name", th: "ชื่อกลุ่มวัสดุ", en: "Material Group Name", type: "text", required: true },
  { key: "brand_code", th: "รหัสประเภทวัสดุ", en: "Material Type Code", type: "text", required: true },
  { key: "brand_name", th: "ชื่อประเภทวัสดุ", en: "Material Type Name (Auto)", type: "auto" },
  { key: "description", th: "คำอธิบาย", en: "Description", type: "text" },
  { key: "is_active", th: "สถานะ", en: "Status", type: "status", required: true },
];

/**
 * Column definitions for the Brand data set (Template No. 05.2) — table `series`.
 * UI name = "Brand" (แบรนด์); payload key ยังเป็น series ตาม API เดิม.
 * No code field — upsert is by `name` (the unique key).
 */
const SERIES_COLUMNS = [
  { key: "no", th: "ลำดับที่", en: "Row No.", type: "auto" },
  { key: "name", th: "ชื่อแบรนด์", en: "Brand Name", type: "text", required: true, unique: true },
  { key: "is_active", th: "สถานะ", en: "Status", type: "status", required: true },
];

/**
 * Column definitions for the Part / Item Master data set (Template No. 09).
 * Sheet "SAPUI5 Export" — ไฟล์ Part Master ที่ export จาก SAP ใช้ได้ทันทีโดยไม่ต้องจัดรูปแบบใหม่:
 * headers on row 1 (ภาษาอังกฤษ), data from row 2, one row = one part. Cells are read by position (A..G).
 * `part_number` is the upsert key (มีแล้ว = อัปเดตตามไฟล์, ยังไม่มี = สร้างใหม่) และห้ามซ้ำในไฟล์.
 * NOTE ชื่อ key ยังเป็น brand_/model_/serie_ ตาม API เดิม — ดู glossary ใน components/global.js
 * คอลัมน์ที่มี `newIfMissing` จับกับ master แบบไม่สนตัวพิมพ์ ค่าที่ยังไม่มี backend สร้างให้ตอนนำเข้า
 * (พรีวิวขึ้นหมายเหตุแทน error): Material Type / Material Group ใช้รหัส SAP เป็นทั้ง code และ name,
 * หน่วยนับใช้ชื่อและรหัสจาก SAP, Brand ใช้ข้อความในไฟล์เป็นชื่อ.
 * `maxLength` = ความยาวคอลัมน์ปลายทางใน DB (ตรงกับ ImportPartDto ฝั่ง backend).
 */
const PART_COLUMNS = [
  { key: "part_number", th: "รหัสอะไหล่", en: "Product Number", type: "text", required: true, unique: true, maxLength: 50, aliases: ["New Product Number"] }, // A
  { key: "name", th: "ชื่ออะไหล่", en: "Product Description", type: "text", required: true, maxLength: 200, aliases: ["Long Text"] },                // B
  { key: "brand_code", th: "ประเภทวัสดุ", en: "Product Type", type: "text", maxLength: 50, aliases: ["MatType"], newIfMissing: "will_create_material_type" },   // C (-> brands.brand_code)
  { key: "model_code", th: "กลุ่มวัสดุ", en: "Product Group", type: "text", maxLength: 50, aliases: ["Mat Group"], newIfMissing: "will_create_material_group" }, // D (-> models.model_code)
  { key: "uom_code", th: "หน่วยนับ", en: "Base Unit of Measure", type: "sap_unit", required: true, newIfMissing: "will_create_uom" },    // E ("Kilogram (KG)" -> uoms.uom_code = KG)
  { key: "old_part_number", th: "รหัสอะไหล่เดิม", en: "Old Product Number", type: "text", maxLength: 50, optionalHeader: true },                   // F (ไม่บังคับ ซ้ำได้)
  { key: "serie_name", th: "แบรนด์", en: "Size / Dimensions", type: "text", maxLength: 200, optionalHeader: true, aliases: ["Size and Dimension"], newIfMissing: "will_create_brand" },           // G (-> series.name)
];

// ความยาวสูงสุดของรหัส / ชื่อหน่วยนับ (uoms.uom_code และ part_masters.uom_name)
const SAP_UNIT_MAX_LENGTH = 50;

/**
 * หน่วยนับของ SAP มาเป็น "ชื่อ (รหัส)" เช่น "Kilogram (KG)" -> { code: "KG", name: "Kilogram" }
 * ใช้วงเล็บชุดท้ายสุด ("Square inch (\"2)" -> "2) ไม่มีวงเล็บเลย = ทั้งข้อความเป็นรหัส และใช้เป็นชื่อด้วย
 */
const parseSapUnit = (text) => {
  const s = String(text ?? "").trim();
  const m = s.match(/^(.*)\(([^()]*)\)$/);
  if (!m) return { code: s, name: s };
  const code = m[2].trim();
  return { code, name: m[1].trim() || code };
};

// หน่วยนับใน master -> รูปแบบเดียวกับไฟล์ SAP เพื่อให้ไฟล์ที่ส่งออกนำกลับเข้ามาได้ทันที
const toSapUnit = (uom) => {
  if (!uom) return "";
  const code = String(uom.uom_code ?? "").trim();
  const name = String(uom.name ?? "").trim();
  return code ? `${name || code} (${code})` : name;
};

/**
 * Column definitions for the Customer data set (Template No. 01).
 * Sheet "Customer Template" — headers on row 7, data starts at row 8 (A..AE, 31 columns).
 * Column order MUST match the template sheet exactly (cells are read by position);
 * the header row of the uploaded file is checked against the bundled template before parsing.
 * Enum columns (type/group/source/contact_prefix/payment_type/currency/sla_level) use
 * `enumKeyMode: "label"` so the value is stored verbatim and matched against the
 * "Reference" sheet (which lists value in column B -> reference.valueColumn = 1).
 * `sla_level` is sent as text and resolved to priority_id (work_requisition_priorities.name_en)
 * on the backend. Status maps to both customer_status (string) and is_active (boolean).
 * Customer fields that the template does not carry (branch_type / branch_code /
 * delivery_zone / sub_district / mobile_phone / payment_terms) are simply not sent —
 * the backend keeps whatever the record already has for them.
 */
const CUSTOMER_COLUMNS = [
  { key: "no", th: "ลำดับที่", en: "Row No.", type: "auto" },                                            // A
  { key: "customer_code", th: "รหัสลูกค้า", en: "Customer Code", type: "text", required: true, unique: true }, // B
  { key: "full_name_th", th: "ชื่อลูกค้า (ไทย)", en: "Customer Name TH", type: "text", required: true },  // C
  { key: "alias_name", th: "ชื่อย่อ", en: "Alias", type: "text" },                                        // D
  { key: "full_name_en", th: "ชื่อลูกค้า (อังกฤษ)", en: "Customer Name EN", type: "text" },              // E
  { key: "type", th: "ประเภทลูกค้า", en: "Customer Type", type: "enum", required: true, enumKeyMode: "label" }, // F
  { key: "group", th: "กลุ่มลูกค้า", en: "Customer Group", type: "enum", enumKeyMode: "label" },          // G
  { key: "tax_number", th: "เลขผู้เสียภาษี", en: "Tax ID", type: "text" },                                // H
  { key: "active_start_date", th: "วันที่เริ่มเป็นลูกค้า", en: "Start Date", type: "date" },              // I
  { key: "source", th: "แหล่งที่มา", en: "Source", type: "enum", enumKeyMode: "label" },                  // J
  { key: "address", th: "ที่อยู่", en: "Address", type: "text" },                                          // K
  { key: "province", th: "จังหวัด", en: "Province", type: "text" },                                        // L
  { key: "district", th: "อำเภอ/เขต", en: "District", type: "text" },                                     // M
  { key: "zip_code", th: "รหัสไปรษณีย์", en: "Postal Code", type: "text" },                               // N
  { key: "phone", th: "โทรศัพท์ 1", en: "Phone 1", type: "text" },                                         // O
  { key: "phone_2", th: "โทรศัพท์ 2", en: "Phone 2", type: "text" },                                      // P
  { key: "fax", th: "โทรสาร", en: "Fax", type: "text" },                                                  // Q
  { key: "email", th: "อีเมล", en: "Email", type: "email" },                                              // R
  { key: "website", th: "เว็บไซต์", en: "Website", type: "text" },                                        // S
  { key: "contact_prefix", th: "คำนำหน้า", en: "Title", type: "enum", enumKeyMode: "label" },             // T
  { key: "contact_first_name", th: "ชื่อผู้ติดต่อหลัก", en: "Primary Contact", type: "text" },             // U
  { key: "contact_last_name", th: "นามสกุล", en: "Last Name", type: "text" },                             // V
  { key: "contact_position", th: "ตำแหน่ง", en: "Position", type: "text" },                               // W
  { key: "contact_phone", th: "โทรศัพท์ผู้ติดต่อ", en: "Contact Phone", type: "text" },                    // X
  { key: "contact_email", th: "อีเมลผู้ติดต่อ", en: "Contact Email", type: "email" },                     // Y
  { key: "payment_type", th: "วิธีการชำระเงิน", en: "Payment Method", type: "enum", enumKeyMode: "label" }, // Z
  { key: "currency", th: "สกุลเงิน", en: "Currency", type: "enum", enumKeyMode: "label" },                // AA
  { key: "sla_level", th: "ระดับ SLA เริ่มต้น", en: "SLA Level", type: "enum", enumKeyMode: "label" },    // AB
  { key: "account_owner", th: "ผู้ดูแลบัญชี", en: "Account Owner", type: "text" },                        // AC
  { key: "remark", th: "หมายเหตุ", en: "Remarks", type: "text" },                                         // AD
  // Tri-state status (Active / Inactive / Hold) -> stored verbatim in customer_status.
  { key: "customer_status", th: "สถานะ", en: "Status", type: "customer_status" },                          // AE
];

/**
 * Column definitions for the WBS data set (Template No. 13).
 * Sheet "project" — a flat export from an external ERP system (SAP): headers on
 * row 1, data starts at row 2, no group titles / instructions / Reference sheet.
 * One row = one WBS element. `project_code` (Project ID) repeats across the rows
 * of the same project, so the backend (admin/wbs/import) upserts the project by
 * `project_code` first (create if missing, otherwise update) and then upserts the
 * WBS by `wbs_element`, linked back to that project. No duplicate check on either
 * column — a repeated key simply updates the existing record.
 */
const WBS_COLUMNS = [
  { key: "entity_guid", th: "รหัสอ้างอิง (Entity GUID)", en: "Entity GUID", type: "text" },
  { key: "project_code", th: "รหัสโครงการ", en: "Project ID", type: "text", required: true },
  { key: "name", th: "ชื่อโครงการ", en: "Project Name", type: "text", required: true },
  { key: "wbs_element", th: "รหัส WBS", en: "WBS Element", type: "text" },
  { key: "wbs_element_name", th: "ชื่อ WBS", en: "WBS Element Name", type: "text" },
];

/**
 * Column definitions for the Project data set (Template No. 16).
 * Sheet "SAPUI5 Export" — the project list exported from SAP: headers on row 1, data from
 * row 2, one row = one project. `project_code` is the upsert key (existing -> update,
 * missing -> create); it is deliberately NOT `unique` here so a code repeated inside the
 * file is not rejected — the later row simply overwrites the earlier one.
 *
 * Dates: SAP writes dd.mm.yyyy or yyyymmdd (and "dd.mm.yyyy, HH:MM:SS" or yyyymmddHHMMSS for Created On, UTC). Depending on how
 * the file was saved the cell arrives as an Excel date serial or as that text — both are
 * accepted (see normDate / normDateTime) and sent to the backend in one canonical form.
 */
const PROJECT_COLUMNS = [
  // ชุดโครงการอ่านตามชื่อหัวคอลัมน์ (cfg.readByHeader) ไม่ใช่ตามตำแหน่ง - ไฟล์ที่ SAP export ลำดับคอลัมน์ไม่คงที่
  // aliases = ชื่อหัวคอลัมน์อื่นที่ยอมรับ (ไม่สนช่องว่าง/ตัวพิมพ์/เครื่องหมาย *) · ตำแหน่ง A-P ด้านล่างคือลำดับในไฟล์ Template/ส่งออกเท่านั้น
  { key: "project_code", th: "รหัสโครงการ", en: "Project", type: "text", required: true },          // A
  { key: "name", th: "ชื่อโครงการ", en: "Project Name", type: "text", required: true },             // B
  { key: "owner_code", th: "รหัสเจ้าของโครงการ", en: "Owner ID", type: "text" },                     // C
  { key: "owner_name", th: "เจ้าของโครงการ", en: "Owner", type: "text" },                           // D
  { key: "planned_start_date", th: "วันเริ่มตามแผน", en: "Planned Start", type: "date" },           // E
  { key: "planned_finish_date", th: "วันสิ้นสุดตามแผน", en: "Planned Finish", type: "date" },       // F
  { key: "actual_start_date", th: "วันเริ่มจริง", en: "Actual Start", type: "date" },               // G
  { key: "actual_finish_date", th: "วันสิ้นสุดจริง", en: "Actual Finish", type: "date" },           // H
  { key: "processing_status", th: "สถานะดำเนินการ", en: "Processing Status", type: "text" },        // I
  { key: "project_manager", th: "ผู้จัดการโครงการ", en: "Project Manager", type: "text" },          // J
  { key: "sap_created_by", th: "ผู้สร้าง (SAP)", en: "Created By", type: "text" },                  // K
  { key: "description", th: "รายละเอียด", en: "Description", type: "text" },                        // L
  { key: "sap_created_on", th: "วันที่สร้าง (UTC)", en: "Created On", type: "datetime", aliases: ["Created On (UTC)"] },     // M
  // ตรงกับ departments.department_code - ใช้หาแผนกของสัญญาดูแลสระในรายงานใกล้หมดประกัน
  // optionalHeader: ไฟล์เก่าที่ยังไม่มีคอลัมน์นี้ต้องนำเข้าได้เหมือนเดิม (ไม่ตรวจหัวคอลัมน์ถ้าไฟล์ว่าง)
  { key: "cost_center", th: "ศูนย์ต้นทุน", en: "Cost Center", type: "text", maxLength: 50, optionalHeader: true, aliases: ["CostCenter"] }, // N
  { key: "controlling_area", th: "Controlling area", en: "Controlling area", type: "text", maxLength: 50, optionalHeader: true }, // O
  { key: "profit_center", th: "Profit Center", en: "Profit Center", type: "text", maxLength: 50, optionalHeader: true },       // P
];

// สถานะดำเนินการใน DB เป็น 3 ค่า (not_started / in_progress / completed) - ส่งออกเป็นข้อความตามภาษาที่ใช้อยู่
// backend แปลงกลับตอนนำเข้าได้ทั้งข้อความไทย/อังกฤษ/ค่าจาก SAP (ดู normalizeProcessingStatus ฝั่ง backend)
const PROCESSING_STATUSES = ["not_started", "in_progress", "completed"];
const processingStatusExportLabel = (value) => (PROCESSING_STATUSES.includes(value) ? t(`processing_status_${value}`) : value || "");

// yyyy-mm-dd (DB) -> dd.mm.yyyy ตามรูปแบบวันที่ของไฟล์ SAP เพื่อให้ไฟล์ที่ส่งออกหน้าตาเหมือนไฟล์ต้นทาง
const toSapDate = (value) => {
  const m = String(value ?? "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : "";
};

// ISO datetime (DB, UTC) -> "dd.mm.yyyy, HH:MM:SS" (UTC) ตามคอลัมน์ "Created On (UTC)"
const toSapDateTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getUTCDate())}.${p(d.getUTCMonth() + 1)}.${d.getUTCFullYear()}, ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())}`;
};


/**
 * Per-data-set import/export configuration.
 * Only `location` is fully wired for now — the remaining data sets fall back to a
 * "not available yet" notice until their templates / endpoints are ready.
 */
// "10099 ประพนธ์ อินชูกุล" -> "10099" (ไฟล์พนักงานของ HR ใส่รหัสตามด้วยชื่อในเซลล์เดียว)
// (ไฟล์ส่งออกรุ่นเก่าเคยมีคอลัมน์ UID ตรงตำแหน่ง X - ค่าที่เป็น UUID ไม่ใช่รหัสผู้ใช้ ข้ามไป)
const UUID_PREFIX = /^[0-9a-f]{8}-[0-9a-f]{4}-/i;
// ไฟล์ส่งออกโครงการรุ่นเก่ามีคอลัมน์ UID ที่ตำแหน่ง N (ปัจจุบันคือ Costcenter) - ค่า UUID ไม่ใช่ศูนย์ต้นทุน ถือว่าว่าง
const costCenterOf = (value) => {
  const v = String(value ?? "").trim();
  return UUID_PREFIX.test(v) ? "" : v;
};

const approverCodeOf = (value) => {
  const code = String(value ?? "").trim().split(/\s+/)[0] || "";
  return /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(code) ? "" : code;
};

/**
 * Column definitions for the Technician data set (Template No. 15).
 * Sheet "Technician Template" — headers on row 7, data starts at row 8 (A..W + optional X..Y approvers).
 * Columns MUST stay in exact A..Y order so cell indices line up with the sheet (new columns are only ever appended).
 * `bank_code`, the two prefix columns and `vehicle_type` are Lookup columns read from the
 * "Reference" sheet (enumKeyMode "label" -> stored verbatim, e.g. "SCB" / "นาย" / "car").
 * `skills` is a comma-separated list of Material Group codes; the backend resolves each code
 * to the model uid stored in technician_skills.item. `department_code` is matched against the
 * department master (code, not name). Vehicle columns describe one vehicle per row and are
 * written to technician_vehicles.
 * Every imported technician gets a login user: `password` blank -> the backend falls back to
 * the technician_code, the user_code is always the technician_code (same rule as the create
 * button), and the role is picked once in the preview modal.
 * Upsert key = technician_code (company-scoped) — รหัสซ้ำจะอัปเดตทับของเดิม ไม่สร้างใหม่.
 */
const TECHNICIAN_COLUMNS = [
  { key: "no", th: "ลำดับที่", en: "Row No.", type: "auto" },                                              // A
  { key: "technician_code", th: "รหัสช่าง", en: "Technician Code", type: "text", required: true, unique: true }, // B
  { key: "perfix_name", th: "คำนำหน้า (ไทย)", en: "Title TH", type: "enum", enumKeyMode: "label" },        // C
  { key: "first_name_th", th: "ชื่อ (ไทย)", en: "First Name TH", type: "text", required: true },           // D
  { key: "last_name_th", th: "นามสกุล (ไทย)", en: "Last Name TH", type: "text", required: true },          // E
  { key: "nickname_th", th: "ชื่อเล่น (ไทย)", en: "Nickname TH", type: "text" },                           // F
  { key: "perfix_name_en", th: "คำนำหน้า (อังกฤษ)", en: "Title EN", type: "enum", enumKeyMode: "label" },  // G
  { key: "first_name_en", th: "ชื่อ (อังกฤษ)", en: "First Name EN", type: "text" },                        // H
  { key: "last_name_en", th: "นามสกุล (อังกฤษ)", en: "Last Name EN", type: "text" },                       // I
  { key: "nickname_en", th: "ชื่อเล่น (อังกฤษ)", en: "Nickname EN", type: "text" },                        // J
  { key: "phone", th: "โทรศัพท์", en: "Phone", type: "text" },                             // K
  { key: "email", th: "อีเมล", en: "Email", type: "text", unique: true },                                  // L (ไม่บังคับ ไม่ตรวจรูปแบบ · ว่างข้ามการตรวจซ้ำ)
  { key: "bank_code", th: "ธนาคาร", en: "Bank", type: "enum", enumKeyMode: "label" },                      // M
  { key: "bank_account_number", th: "เลขที่บัญชีธนาคาร", en: "Bank Account No.", type: "text" },           // N
  { key: "bank_account_name", th: "ชื่อบัญชีธนาคาร", en: "Bank Account Name", type: "text" },              // O
  { key: "vehicle_type", th: "ประเภทพาหนะ", en: "Vehicle Type", type: "enum", enumKeyMode: "label" },      // P
  { key: "vehicle_brand_model", th: "ยี่ห้อ / รุ่นพาหนะ", en: "Vehicle Brand / Model", type: "text" },     // Q
  { key: "vehicle_license_plate", th: "ป้ายทะเบียน", en: "License Plate", type: "text" },                  // R
  { key: "skills", th: "ความชำนาญ (รหัสกลุ่มวัสดุ)", en: "Skills (Material Group Codes)", type: "list" },  // S (lookup)
  { key: "department_code", th: "รหัสแผนก", en: "Department Code", type: "text", required: true },         // T (lookup)
  { key: "password", th: "รหัสผ่าน", en: "Password", type: "text" },                                       // U
  { key: "status", th: "สถานะ", en: "Status", type: "status", required: true },                            // V
  { key: "rate_per_hour", th: "อัตราค่าแรง/ชั่วโมง", en: "Rate/Hour", type: "number", min: 0 },            // W
  // ผู้บังคับบัญชาระดับ 1 / 2 (รหัสผู้ใช้) - ไฟล์ HR ใส่ "รหัส ชื่อ" ได้ ระบบใช้เฉพาะรหัสตัวแรก
  // optionalHeader: ไฟล์เก่าที่ไม่มีคอลัมน์นี้ต้องนำเข้าได้เหมือนเดิม · ว่าง = ไม่เปลี่ยนค่าเดิม
  { key: "approver1_code", th: "ผู้อนุมัติลำดับที่ 1", en: "Approver 1", type: "text", maxLength: 200, optionalHeader: true }, // X
  { key: "approver2_code", th: "ผู้อนุมัติลำดับที่ 2", en: "Approver 2", type: "text", maxLength: 200, optionalHeader: true }, // Y
];

/**
 * Column definitions for the Asset / Machine data set (Template No. 10).
 * Sheet "Data Template" — row 7 = section titles, row 8 = column headers -> data starts at row 9.
 * Columns MUST stay in exact A..AJ order so cell indices line up with the sheet.
 * `th` / `en` are copied verbatim from the template header so the preview table reads exactly
 * like the file (the template's own TH/EN pairing for I/J is inconsistent — keys carry the
 * correct meaning: I = Material Group / models, J = Material Type / brands. See the glossary
 * in components/global.js).
 * Lookup columns carry the master's CODE and are resolved to ids on the backend:
 *   project_code -> projects · department_code -> departments · customer_code -> customers
 *   sale_code -> sale_masters · part_number -> part_masters
 * Auto columns (G-J, Y, AG, AJ) are filled by the system on export and ignored on import,
 * matching the note printed on the template header.
 * Upsert key = serial_number (company-scoped) — หมายเลขเครื่องเดิมจะอัปเดตทับ ไม่สร้างใหม่
 * และ asset_code เป็นเลขรันนิ่งที่ระบบออกให้ตอนสร้างเท่านั้น จึงไม่มีคอลัมน์นี้ในไฟล์.
 */
const ASSET_COLUMNS = [
  { key: "no", th: "ลำดับที่", en: "No.", type: "auto" },                                                  // A
  { key: "project_code", th: "โครงการ", en: "Project", type: "text", required: true },                     // B (lookup)
  { key: "department_code", th: "แผนก", en: "Department", type: "text" },                                  // C (lookup)
  { key: "customer_code", th: "รหัสลูกค้า", en: "Customer ID", type: "text", required: true },             // D (lookup)
  { key: "sale_code", th: "พนักงานขาย", en: "Sales ID", type: "text" },                                    // E (lookup)
  { key: "part_number", th: "ITEM ID (รหัสสินค้า)", en: "Item ID", type: "text", required: true },          // F (lookup)
  { key: "part_name", th: "ชื่อสินค้า", en: "Product Name", type: "auto" },                                // G
  { key: "serie_name", th: "ยี่ห้อ", en: "Brand", type: "auto" },                                          // H
  { key: "model_name", th: "กลุ่มวัสดุ", en: "Material Type", type: "auto" },                               // I (= models)
  { key: "brand_name", th: "ประเภทวัสดุ", en: "Material Group", type: "auto" },                             // J (= brands)
  { key: "serial_number", th: "หมายเลขเครื่อง", en: "Serial No.", type: "text", required: true, unique: true }, // K
  { key: "price", th: "ราคาเครื่อง", en: "Machine Price", type: "number" },                                // L
  { key: "is_active", th: "สถานะ", en: "Status", type: "status", required: true },                         // M
  { key: "replacement_period", th: "รอบการเปลี่ยนอะไหล่", en: "Replacement Period", type: "number" },      // N
  { key: "location_running_number", th: "รหัสสถานที่", en: "Location ID", type: "text" },                  // O
  { key: "location_name", th: "ชื่อสถานที่", en: "Location Name", type: "text" },                          // P
  { key: "location_address", th: "ที่อยู่", en: "Address", type: "text" },                                  // Q
  { key: "location_province", th: "จังหวัด", en: "Province", type: "text" },                                // R
  { key: "location_district", th: "อำเภอ", en: "District", type: "text" },                                  // S
  { key: "location_sub_district", th: "ตำบล", en: "Sub-district", type: "text" },                          // T
  { key: "location_zip_code", th: "รหัสไปรษณีย์", en: "Postal Code", type: "text" },                       // U
  { key: "location_floor_room", th: "ชั้น / ห้อง", en: "Floor / Room", type: "text" },                     // V
  { key: "latitude", th: "ละติจูด", en: "Latitude", type: "number" },                                       // W
  { key: "longitude", th: "ลองจิจูด", en: "Longitude", type: "number" },                                    // X
  { key: "map_pin", th: "ปักหมุดจากแผนที่", en: "Pin from Map", type: "auto" },                             // Y (no field on the asset)
  { key: "contract_name", th: "ชื่อผู้ติดต่อ", en: "Contract Name", type: "text" },                        // Z
  { key: "contract_phone", th: "เบอร์โทรศัพท์", en: "Phone Number", type: "text" },                         // AA
  { key: "contract_email", th: "อีเมล", en: "Email", type: "email" },                                       // AB
  { key: "location_remark", th: "หมายเหตุสถานที่", en: "Location Note", type: "text" },                     // AC
  { key: "invoice_number", th: "เลขใบกำกับภาษี", en: "Invoice No", type: "text" },                          // AD
  { key: "invoice_date", th: "วันที่ใบกำกับภาษี", en: "Invoice Date (dd/mm/yyyy)", type: "date" },          // AE
  { key: "coi_date", th: "วันที่ติดตั้ง (EOI)", en: "EOI Date (dd/mm/yyyy)", type: "date" },                // AF
  // วันเริ่มประกันงวดล่าสุด (customer_warranty_renew_date) นำเข้าได้ ส่งออกว่างใช้วันที่ติดตั้ง (EOI)
  { key: "warranty_start_date", th: "วันที่เริ่มประกัน", en: "Warranty Start Date (dd/mm/yyyy)", type: "date" }, // AG
  { key: "customer_warranty_month", th: "ระยะเวลาประกันลูกค้า (เดือน)", en: "Customer Warranty Ext. (Month)", type: "number", aliases: ["Customer Ext. (Month)"] }, // AH
  { key: "customer_warranty_expire_date", th: "วันหมดประกันลูกค้า", en: "Customer Warranty End (dd/mm/yyyy)", type: "date", aliases: ["Customer End (dd/mm/yyyy)"] },   // AI
  // จำนวนงวดประกันคำนวณจากรายการประกันจริง (หน้าทรัพย์สินก็แสดงเป็นช่องอ่านอย่างเดียว)
  // ไฟล์พาข้อมูลประกันมาได้งวดเดียวต่อแถว จึงส่งออกอย่างเดียว ไม่นำเข้า
  { key: "customer_warranty_count", th: "จำนวนครั้งที่ต่ออายุการรับประกัน", en: "Warranty Ext. (Renewal Count)", type: "auto", aliases: ["จำนวนครั้งที่ต่ออายุการ"] }, // AJ
];

/**
 * ISO / Date -> "dd/mm/yyyy" ตามรูปแบบที่ template กำหนด (ค่าว่างคืนสตริงว่าง)
 * ใช้ตอนส่งออกเท่านั้น — ขานำเข้าใช้ normDate ซึ่งรับทั้ง dd/mm/yyyy, ISO และ serial ของ Excel
 */
const toTemplateDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

// key ชุดข้อมูล -> permission ที่ backend ใช้ตรวจที่ route /import ของชุดนั้น (ต้องตรงกับ checkPermission ใน route)
const IMPORT_PERMISSION = {
  customer: "customers.create",
  technician: "technicians.create",
  part_master: "part-master.create",
  asset: "asset.create",
  warehouse: "warehouse.create",
  location: "location.create",
  uom: "uoms.create",
  category: "part-categories.create",
  brand: "brands.create",
  model: "models.create",
  series: "serie.create",
  project: "projects.create",
  wbs: "projects.create",
  stock: ["stock.import", "stock.create"], // หนึ่งในนี้พอ (ตรงกับ checkAnyPermission ใน stock.route)
};
const canImport = (key) => (IMPORT_PERMISSION[key] ? canAny(IMPORT_PERMISSION[key]) : true);

const DATASET_CONFIG = {
  customer: {
    endpoint: "admin/customers",
    importPath: "admin/customers/import", // dedicated bulk upsert by customer_code
    templateAsset: customerTemplateFile,
    templateFile: "01_Customer_Template.xlsx",
    exportFile: "Customer_Export.xlsx",
    sheetName: "Customer Template",
    headerRows: 7, // rows 1-7 are header/instructions; data starts at row 8
    columns: CUSTOMER_COLUMNS,
    // Reference sheet lists values in column B (column A is the running No.) -> valueColumn 1.
    reference: {
      sheetMatch: "Reference",
      valueColumn: 1,
      sections: [
        { columnKey: "type", startsWith: "ประเภทลูกค้า" },
        { columnKey: "group", startsWith: "กลุ่มลูกค้า" },
        { columnKey: "source", startsWith: "แหล่งที่มา" },
        { columnKey: "contact_prefix", startsWith: "คำนำหน้า" },
        { columnKey: "payment_type", startsWith: "วิธีการชำระเงิน" },
        { columnKey: "currency", startsWith: "สกุลเงิน" },
        { columnKey: "sla_level", startsWith: "ระดับ SLA" },
      ],
    },
    // API record -> row aligned to `columns` (display labels match the template/Reference).
    toRow: (item, idx) => [
      idx + 1,
      item.customer_code || "",
      item.full_name_th || "",
      item.alias_name || "",
      item.full_name_en || "",
      item.type || "",
      item.group || "",
      item.tax_number || "",
      item.active_start_date || "",
      item.source || "",
      item.address || "",
      item.province || "",
      item.district || "",
      item.zip_code || "",
      item.phone || "",
      item.phone_2 || "",
      item.fax || "",
      item.email || "",
      item.website || "",
      item.contact_prefix || "",
      item.contact_first_name || "",
      item.contact_last_name || "",
      item.contact_position || "",
      item.contact_phone || "",
      item.contact_email || "",
      item.payment_type || "",
      item.currency || "",
      item.priority?.name_en || "",
      item.account_owner || "",
      item.remark || "",
      item.customer_status || (item.is_active ? "Active" : "Inactive"),
    ],
    // validated cells -> upsert payload (priority resolved from sla_level on the backend).
    // Only the template's columns are sent; fields outside the template are left untouched.
    toPayload: (cells) => ({
      company_id: company?.id || "",
      customer_code: cells.customer_code.value,
      full_name_th: cells.full_name_th.value,
      full_name_en: cells.full_name_en?.value || "",
      alias_name: cells.alias_name?.value || "",
      type: cells.type.value,
      group: cells.group?.value || "",
      tax_number: cells.tax_number?.value || "",
      active_start_date: cells.active_start_date?.value || "",
      source: cells.source?.value || "",
      address: cells.address.value,
      province: cells.province.value,
      district: cells.district?.value || "",
      zip_code: cells.zip_code?.value || "",
      phone: cells.phone?.value || "",
      phone_2: cells.phone_2?.value || "",
      fax: cells.fax?.value || "",
      email: cells.email?.value || "",
      website: cells.website?.value || "",
      contact_prefix: cells.contact_prefix?.value || "",
      contact_first_name: cells.contact_first_name.value,
      contact_last_name: cells.contact_last_name?.value || "",
      contact_position: cells.contact_position?.value || "",
      contact_phone: cells.contact_phone?.value || "",
      contact_email: cells.contact_email?.value || "",
      payment_type: cells.payment_type?.value || "",
      currency: cells.currency?.value || "",
      sla_level: cells.sla_level?.value || "",
      account_owner: cells.account_owner?.value || "",
      remark: cells.remark?.value || "",
      customer_status: cells.customer_status.value, // "Active" | "Inactive" | "Hold"
      is_active: cells.customer_status.value === "Active",
    }),
  },

  technician: {
    endpoint: "admin/technicians",
    importPath: "admin/technicians/import", // dedicated bulk upsert (technician_code + company)
    templateAsset: technicianTemplateFile,
    templateFile: "15_Technician_Template.xlsx",
    exportFile: "Technician_Export.xlsx",
    sheetName: "Technician Template",
    headerRows: 7, // rows 1-7 are header/instructions; data starts at row 8
    columns: TECHNICIAN_COLUMNS,
    // Reference sheet lists values in column B (column A is the running No.) -> valueColumn 1.
    reference: {
      sheetMatch: "Reference",
      valueColumn: 1,
      sections: [
        { columnKey: "perfix_name", startsWith: "คำนำหน้าชื่อ (ไทย)" },
        { columnKey: "perfix_name_en", startsWith: "คำนำหน้าชื่อ (อังกฤษ)" },
        { columnKey: "bank_code", startsWith: "รหัสธนาคาร" },
        { columnKey: "vehicle_type", startsWith: "ประเภทพาหนะ" },
        { columnKey: "status", startsWith: "สถานะ" },
      ],
    },
    // บทบาทไม่ได้อยู่ในไฟล์ (เป็นเรื่องสิทธิ์ ไม่ใช่ข้อมูลช่าง และไฟล์หนึ่งชุดมักเป็นบทบาทเดียวกันทั้งไฟล์)
    // -> ให้เลือกครั้งเดียวใน modal ก่อนยืนยัน แล้วส่งไปที่ระดับ body ใช้กับทุกแถว
    bulkOption: {
      bodyField: "role_uid",
      label: "role",
      placeholder: "select_role",
      hint: "import_role_hint",
      endpoint: "admin/roles",
      valueField: "uid",
      labelField: "name",
    },
    // แต่ละ chunk สร้าง user (login) ให้ช่างใหม่ด้วย และ user_code เดินเลขจากค่าล่าสุดใน DB
    // -> ต้องส่งทีละ chunk ไม่งั้น chunk ที่วิ่งพร้อมกันจะได้ user_code ชนกัน
    sequentialImport: true,
    // Company-scoped masters checked in the preview; the backend re-resolves them on save.
    // ทั้งสองตัว backend resolve แบบไม่สนตัวพิมพ์ ฝั่งพรีวิวจึงต้องเทียบแบบเดียวกัน
    lookups: {
      skills: { endpoint: "warehouse/models", field: "model_code", lower: true },
      department_code: { endpoint: "admin/departments", field: "department_code", lower: true },
    },
    // technician_skills.item เก็บเป็น uid ของกลุ่มวัสดุ -> ตอน export ต้อง map กลับเป็นรหัส
    // เพื่อให้ไฟล์ที่ส่งออกนำกลับเข้ามา import ซ้ำได้ทันที
    exportLookups: {
      skills: { endpoint: "warehouse/models", field: "uid", mapField: "model_code" },
    },
    // ระบุประเภทพาหนะแล้วต้องมีป้ายทะเบียนด้วย (กฎเดียวกับปุ่มเพิ่มพาหนะในหน้าช่าง)
    postValidate: (rows) => {
      rows.forEach((r) => {
        const type = (r.cells.vehicle_type?.raw || "").trim();
        const plate = r.cells.vehicle_license_plate;
        if (type && plate && !(plate.raw || "").trim()) {
          plate.error = "required";
          r.valid = false;
        }
      });
      return rows;
    },
    // Per-row display/normalization applied after validation:
    //  - Bank Account Name defaults to the technician's Thai full name when blank (same as the form)
    //  - the password is masked in the preview table
    enrichRows: (rows) => {
      const isBlank = (v) => { const s = String(v ?? "").trim(); return s === "" || s === "-"; };
      rows.forEach((r) => {
        const c = r.cells;
        if (c.bank_account_name && isBlank(c.bank_account_name.raw)) {
          const name = `${c.first_name_th?.value || ""} ${c.last_name_th?.value || ""}`.trim();
          if (name) { c.bank_account_name.display = name; c.bank_account_name.value = name; }
        }
        // รหัสผ่านจริงไม่ควรโชว์ในตาราง ส่วนช่องที่เว้นว่างบอกให้เห็นว่าระบบจะใช้รหัสช่างแทน
        if (c.password) {
          c.password.display = String(c.password.raw ?? "").trim() !== ""
            ? "••••••"
            : `${t("import_password_default_hint")}: ${c.technician_code?.value || ""}`;
        }
      });
      return rows;
    },
    // API record -> row aligned to `columns` (display labels match the template/Reference).
    // Skill items are Material Group uids -> mapped back to codes via `exportLookups`.
    // พาหนะส่งออกคันแรก (template รองรับ 1 คันต่อแถว)
    toRow: (item, idx, ctx) => {
      const codeOf = ctx?.maps?.skills;
      const skills = (item.technician_skill || [])
        .map((s) => (codeOf ? codeOf.get(String(s.item || "")) : "") || "")
        .filter(Boolean)
        .join(", ");
      const vehicle = (item.technician_vehicle || [])[0] || {};
      return [
        idx + 1,
        item.technician_code || "",
        item.perfix_name || "",
        item.first_name_th || "",
        item.last_name_th || "",
        item.nickname_th || "",
        item.perfix_name_en || "",
        item.first_name_en || "",
        item.last_name_en || "",
        item.nickname_en || "",
        item.phone || "",
        item.email || "",
        item.bank_code || "",
        item.bank_account_number || "",
        item.bank_account_name || "",
        vehicle.vehicle_type || "",
        vehicle.brand_model || "",
        vehicle.license_plate || "",
        skills,
        item.department_code || "",
        "", // Password is never exported
        item.is_active ? "Active" : "Inactive",
        item.rate_per_hour ?? "",
        // "รหัส ชื่อ" - นำกลับเข้ามา import ซ้ำได้ (backend ใช้เฉพาะรหัสตัวแรก)
        [item.supervisor_code, item.supervisor_name].filter(Boolean).join(" "),
        [item.supervisor2_code, item.supervisor2_name].filter(Boolean).join(" "),
      ];
    },
    // validated cells -> upsert payload (skills / department resolved on the backend)
    toPayload: (cells) => {
      const bank = (global.bank || []).find((b) => b.value === cells.bank_code?.value);
      return {
        company_id: company?.id || "",
        technician_code: cells.technician_code.value,
        perfix_name: cells.perfix_name?.value || "",
        perfix_name_en: cells.perfix_name_en?.value || "",
        first_name_th: cells.first_name_th.value,
        last_name_th: cells.last_name_th.value,
        first_name_en: cells.first_name_en?.value || "",
        last_name_en: cells.last_name_en?.value || "",
        nickname_th: cells.nickname_th?.value || "",
        nickname_en: cells.nickname_en?.value || "",
        phone: cells.phone.value,
        email: cells.email.value,
        bank_code: cells.bank_code?.value || "",
        bank_name: bank?.name_th || cells.bank_code?.value || "",
        bank_account_number: cells.bank_account_number?.value || "",
        bank_account_name: cells.bank_account_name?.value || "",
        vehicle_type: cells.vehicle_type?.value || "",
        vehicle_brand_model: cells.vehicle_brand_model?.value || "",
        vehicle_license_plate: cells.vehicle_license_plate?.value || "",
        skills: Array.isArray(cells.skills?.value) ? cells.skills.value : [],
        department_code: cells.department_code.value,
        password: cells.password?.value || "",
        status: cells.status.value === true ? "active" : "inactive",
        is_active: cells.status.value === true,
        // ว่าง = ไม่ส่ง key (backend คงค่าเดิมของช่าง)
        ...(String(cells.rate_per_hour?.value ?? "").trim() !== "" ? { rate_per_hour: Number(cells.rate_per_hour.value) } : {}),
        // ผู้อนุมัติ: เฉพาะที่มีค่า (ว่าง = ไม่ส่ง key backend คงค่าเดิม) · ตัด "รหัส ชื่อ" เหลือรหัสตัวแรก
        ...(approverCodeOf(cells.approver1_code?.value) ? { approver1_code: approverCodeOf(cells.approver1_code.value) } : {}),
        ...(approverCodeOf(cells.approver2_code?.value) ? { approver2_code: approverCodeOf(cells.approver2_code.value) } : {}),
      };
    },
  },

  asset: {
    endpoint: "admin/assets",
    importPath: "admin/assets/import", // bulk upsert by serial_number + resolve relations
    templateAsset: assetTemplateFile,
    templateFile: "10_Asset_Template.xlsx",
    exportFile: "Asset_Export.xlsx",
    sheetName: "Data Template",
    headerRows: 8, // row 7 = section titles, row 8 = column headers -> data from row 9
    columns: ASSET_COLUMNS,
    // asset_code เป็นเลขรันนิ่งต่อ prefix ที่อ่านจากรหัสล่าสุดใน DB -> chunk ที่วิ่งพร้อมกัน
    // จะได้เลขชนกัน (เหตุผลเดียวกับ user_code ของชุดข้อมูลช่าง) จึงต้องส่งทีละ chunk
    sequentialImport: true,
    // Company-scoped masters checked in the preview; the backend re-resolves them on save.
    // รหัสแผนก backend resolve แบบไม่สนตัวพิมพ์ ฝั่งพรีวิวจึงต้องเทียบแบบเดียวกัน
    lookups: {
      project_code: { endpoint: "admin/projects", field: "project_code" },
      department_code: { endpoint: "admin/departments", field: "department_code", lower: true },
      customer_code: { endpoint: "admin/customers", field: "customer_code" },
      sale_code: { endpoint: "admin/sale-masters", field: "sale_code" },
      part_number: { endpoint: "warehouse/part-masters", field: "part_number" },
    },
    // API record -> row aligned to `columns`. ชื่อสินค้า/ยี่ห้อ/กลุ่มวัสดุ/ประเภทวัสดุ มาจาก Item Master
    // ("ยี่ห้อ" = แบรนด์ ซึ่ง API ทรัพย์สินไม่ได้ส่งกลับมา -> ปล่อยว่างไว้ ไม่กระทบการนำเข้าเพราะเป็นคอลัมน์ auto)
    // ประกันลูกค้าในไฟล์มีงวดเดียว -> ส่งออกงวดล่าสุด ซึ่งเป็นงวดที่ยังมีผลอยู่
    toRow: (item, idx) => {
      const warranties = item.customer_warranty || [];
      const warranty = warranties[warranties.length - 1] || {};
      return [
        idx + 1,
        item.project?.project_code || "",
        item.department?.department_code || "",
        item.customer?.customer_code || "",
        item.sale?.sale_code || "",
        item.part_number || item.part?.part_number || "",
        item.part_name || item.part?.name || "",
        "",
        item.part?.model?.name || "",
        item.part?.brand?.name || "",
        item.serial_number || "",
        item.price ?? "",
        item.is_active ? "Active" : "Inactive",
        item.replacement_period ?? "",
        item.location_running_number || "",
        item.location_name || "",
        item.location_address || "",
        item.location_province || "",
        item.location_district || "",
        item.location_sub_district || "",
        item.location_zip_code || "",
        item.location_floor_room || "",
        item.latitude || "",
        item.longitude || "",
        "",
        item.contract_name || "",
        item.contract_phone || "",
        item.contract_email || "",
        item.location_remark || "",
        item.invoice_number || "",
        toTemplateDate(item.invoice_date),
        toTemplateDate(item.coi_date),
        toTemplateDate(warranty.customer_warranty_renew_date || item.coi_date), // งวดล่าสุด (งวดเดียวกับเดือน/วันหมด) renew_date = วันเริ่มประกัน, ว่างใช้วันติดตั้ง
        warranty.customer_warranty_month ?? "",
        toTemplateDate(warranty.customer_warranty_expire_date),
        item.customer_warranty_count ?? "",
      ];
    },
    // validated cells -> upsert payload (relations resolved from their codes on the backend)
    toPayload: (cells) => {
      const num = (k) => {
        const v = cells[k]?.value;
        return v !== "" && v != null && isFinite(Number(v)) ? Number(v) : null;
      };
      const int = (k) => {
        const n = num(k);
        return n == null ? null : Math.trunc(n);
      };
      const money = (k) => {
        const n = num(k);
        return n == null ? null : Math.round(n * 100) / 100;
      };
      return {
        company_id: company?.id || "",
        project_code: cells.project_code.value,
        department_code: cells.department_code?.value || "",
        customer_code: cells.customer_code.value,
        sale_code: cells.sale_code?.value || "",
        part_number: cells.part_number.value,
        serial_number: cells.serial_number.value,
        price: money("price"),
        replacement_period: int("replacement_period"),

        location_running_number: cells.location_running_number?.value || "",
        location_name: cells.location_name?.value || "",
        location_address: cells.location_address?.value || "",
        location_province: cells.location_province?.value || "",
        location_district: cells.location_district?.value || "",
        location_sub_district: cells.location_sub_district?.value || "",
        location_zip_code: cells.location_zip_code?.value || "",
        location_floor_room: cells.location_floor_room?.value || "",
        latitude: cells.latitude?.value != null ? String(cells.latitude.value) : "",
        longitude: cells.longitude?.value != null ? String(cells.longitude.value) : "",
        location_remark: cells.location_remark?.value || "",

        contract_name: cells.contract_name?.value || "",
        contract_phone: cells.contract_phone?.value || "",
        contract_email: cells.contract_email?.value || "",

        invoice_number: cells.invoice_number?.value || "",
        invoice_date: cells.invoice_date?.value || "",
        coi_date: cells.coi_date?.value || "",

        warranty_start_date: cells.warranty_start_date?.value || "",
        customer_warranty_month: int("customer_warranty_month"),
        customer_warranty_expire_date: cells.customer_warranty_expire_date?.value || "",

        is_active: cells.is_active.value === true,
      };
    },
  },

  location: {
    endpoint: "warehouse/locations",
    importPath: "warehouse/locations/import", // bulk upsert endpoint (implemented)
    templateAsset: locationTemplateFile,
    templateFile: "12_Location_Template.xlsx",
    exportFile: "Location_Export.xlsx",
    sheetName: "Data Template",
    headerRows: 7, // rows 1-7 are header/instructions; data starts at row 8
    columns: LOCATION_COLUMNS,
    // Reference sheet — valid values for enum/status columns are read from here.
    reference: {
      sheetMatch: "Reference",
      sections: [
        { columnKey: "location_type", startsWith: "ประเภทตำแหน่ง" },
        { columnKey: "is_active", startsWith: "สถานะ" },
      ],
    },
    // API record -> row aligned to `columns` (use display labels so it matches the template/Reference)
    toRow: (item, idx) => [
      idx + 1,
      item.location_code || "",
      item.name || "",
      item.location_type ? t(item.location_type) : "",
      item.address || "",
      item.latitude || "",
      item.longitude || "",
      item.description || "",
      item.is_active ? "เปิดใช้งาน" : "ปิดใช้งาน",
    ],
    // validated cells -> create payload
    toPayload: (cells) => ({
      company_id: company?.id || "",
      location_code: cells.location_code.value,
      name: cells.name.value,
      location_type: cells.location_type.value,
      address: cells.address?.value || "",
      latitude: cells.latitude?.value ? String(cells.latitude.value) : "",
      longitude: cells.longitude?.value ? String(cells.longitude.value) : "",
      description: cells.description?.value || "",
      is_active: cells.is_active.value === true,
    }),
  },

  warehouse: {
    endpoint: "warehouse/warehouses",
    importPath: "warehouse/warehouses/import", // bulk upsert + links storage locations
    templateAsset: warehouseTemplateFile,
    templateFile: "11_Warehouse_Template.xlsx",
    exportFile: "Warehouse_Export.xlsx",
    sheetName: "Data Template",
    headerRows: 7,
    columns: WAREHOUSE_COLUMNS,
    reference: {
      sheetMatch: "Reference",
      sections: [
        { columnKey: "is_active", startsWith: "สถานะ" },
      ],
    },
    // Lookups fetched (company-scoped) before validation -> { columnKey: { endpoint, field } }.
    lookups: {
      // ไม่ส่ง is_active = ได้ทุกสถานะ เพื่อแยก "ไม่พบ" ออกจาก "ปิดใช้งาน"
      branch: { endpoint: "warehouse/branches", field: "name", lower: true, statusField: "is_active" },
      storage_locations: { endpoint: "warehouse/locations", field: "location_code" },
    },
    toRow: (item, idx) => [
      idx + 1,
      item.branch || "",
      item.warehouse_code || "",
      item.name || "",
      item.description || "",
      (item.locations || []).map((l) => l.location_code).join(", "),
      item.is_active ? "เปิดใช้งาน" : "ปิดใช้งาน",
    ],
    toPayload: (cells) => ({
      company_id: company?.id || "",
      branch: cells.branch.value,
      warehouse_code: cells.warehouse_code.value,
      name: cells.name.value,
      description: cells.description?.value || "",
      storage_locations: Array.isArray(cells.storage_locations?.value)
        ? cells.storage_locations.value
        : [],
      is_active: cells.is_active.value === true,
    }),
  },

  uom: {
    endpoint: "warehouse/uoms",
    importPath: "warehouse/uoms/import", // bulk upsert by uom_code
    templateAsset: uomTemplateFile,
    templateFile: "07_UnitOfMeasure_Template.xlsx",
    exportFile: "UnitOfMeasure_Export.xlsx",
    sheetName: "Unit Template",
    headerRows: 7,
    columns: UOM_COLUMNS,
    toRow: (item, idx) => [
      idx + 1,
      item.uom_code || "",
      item.name || "",
      item.symbol || "",
      item.description || "",
      item.is_active ? "Active" : "Inactive",
    ],
    toPayload: (cells) => ({
      company_id: company?.id || "",
      uom_code: cells.uom_code.value,
      name: cells.name.value,
      symbol: cells.symbol?.value || "",
      description: cells.description?.value || "",
      is_active: cells.is_active.value === true,
    }),
  },

  category: {
    endpoint: "warehouse/part-categories",
    importPath: "warehouse/part-categories/import", // bulk upsert by category_code
    templateAsset: categoryTemplateFile,
    templateFile: "08_Category_Template.xlsx",
    exportFile: "Category_Export.xlsx",
    sheetName: "Category Template",
    headerRows: 8, // row 7 = group titles, row 8 = column headers -> data from row 9
    columns: CATEGORY_COLUMNS,
    toRow: (item, idx) => {
      const fc = Array.isArray(item.field_control) ? item.field_control : [];
      const yn = (k) => (fc.includes(k) ? "Yes" : "No");
      return [
        idx + 1,
        item.category_code || "",
        item.name || "",
        item.description || "",
        yn("from_warehouse_id"),
        yn("location_id"),
        yn("lot_id"),
        yn("lot_received_date"),
        item.is_active ? "Active" : "Inactive",
      ];
    },
    toPayload: (cells) => {
      const field_control = [];
      if (cells.warehouse_control?.value === true) field_control.push("from_warehouse_id");
      if (cells.location_control?.value === true) field_control.push("location_id");
      if (cells.batch_control?.value === true) field_control.push("lot_id");
      if (cells.date_control?.value === true) field_control.push("lot_received_date");
      return {
        company_id: company?.id || "",
        category_code: cells.category_code.value,
        name: cells.name.value,
        description: cells.description?.value || "",
        field_control,
        is_active: cells.is_active.value === true,
      };
    },
  },

  // "Material Type" (ประเภทวัสดุ) — table `brands`; key/endpoint คงเดิมตาม API
  brand: {
    endpoint: "warehouse/brands",
    importPath: "warehouse/brands/import", // bulk upsert by brand_code (ไม่แตะ parent/level)
    templateAsset: materialTypeTemplateFile,
    templateFile: "04_Material_Type_Template.xlsx",
    exportFile: "MaterialType_Export.xlsx",
    sheetName: "Material Type Template", // ชื่อ sheet ในไฟล์ template ห้ามเปลี่ยน
    headerRows: 7,
    columns: BRAND_COLUMNS,
    // Nested (tops + children) -> flat rows so ทุกระดับถูก export ครบ.
    // ไม่มีคอลัมน์ parent แล้ว — ตอน import กลับเข้ามา backend จะคง parent/level เดิมไว้.
    flattenForExport: (data) => {
      const out = [];
      (data || []).forEach((top) => {
        out.push(top);
        (top.children || []).forEach((ch) => out.push(ch));
      });
      return out;
    },
    toRow: (item, idx) => [
      idx + 1,
      item.brand_code || "",
      item.name || "",
      item.description || "",
      item.is_active ? "Active" : "Inactive",
    ],
    toPayload: (cells) => ({
      company_id: company?.id || "",
      brand_code: cells.brand_code.value,
      name: cells.name.value,
      description: cells.description?.value || "",
      is_active: cells.is_active.value === true,
    }),
  },

  // "Material Group" (กลุ่มวัสดุ) — table `models`; key/endpoint คงเดิมตาม API
  model: {
    endpoint: "warehouse/models",
    importPath: "warehouse/models/import", // bulk upsert by model_code + brand_code -> brand_id
    templateAsset: materialGroupTemplateFile,
    templateFile: "05.1_Material_Group_Template.xlsx",
    exportFile: "MaterialGroup_Export.xlsx",
    sheetName: "Material Group Template", // ชื่อ sheet ในไฟล์ template ห้ามเปลี่ยน
    headerRows: 7,
    columns: MODEL_COLUMNS,
    // brand_code must reference a real Material Type (any level). The tree is nested -> flatten.
    lookups: {
      brand_code: {
        endpoint: "warehouse/brands",
        field: "brand_code",
        flatten: (data) => (data || []).flatMap((b) => [b, ...(b.children || [])]),
      },
    },
    toRow: (item, idx) => [
      idx + 1,
      item.model_code || "",
      item.name || "",
      item.brand?.brand_code || "",
      item.brand?.name || "",
      item.description || "",
      item.is_active ? "Active" : "Inactive",
    ],
    toPayload: (cells) => ({
      company_id: company?.id || "",
      model_code: cells.model_code.value,
      name: cells.name.value,
      brand_code: cells.brand_code.value,
      description: cells.description?.value || "",
      is_active: cells.is_active.value === true,
    }),
  },

  // "Brand" (แบรนด์) — table `series`; key/endpoint คงเดิมตาม API
  series: {
    endpoint: "warehouse/series",
    importPath: "warehouse/series/import", // bulk upsert by name (no code field)
    templateAsset: brandTemplateFile,
    templateFile: "05.2_Brand_Template.xlsx",
    exportFile: "Brand_Export.xlsx",
    sheetName: "Brand Template", // ชื่อ sheet ในไฟล์ template ห้ามเปลี่ยน
    headerRows: 7,
    columns: SERIES_COLUMNS,
    toRow: (item, idx) => [
      idx + 1,
      item.name || "",
      item.is_active ? "Active" : "Inactive",
    ],
    toPayload: (cells) => ({
      company_id: company?.id || "",
      name: cells.name.value,
      is_active: cells.is_active.value === true,
    }),
  },

  /**
   * อะไหล่ (Template No. 09) — ไฟล์ Part Master ที่ export จาก SAP: 1 แถว = 1 อะไหล่
   * upsert ด้วย part_number; ฟิลด์ที่ไฟล์ไม่มี (หมวดหมู่ ราคา จุดสั่งซื้อ ฯลฯ) backend คงค่าเดิมไว้
   */
  part_master: {
    endpoint: "warehouse/part-masters",
    importPath: "warehouse/part-masters/import", // upsert by part_number + สร้าง master ที่ยังไม่มีให้
    // master ที่ยังไม่มีถูกสร้างตอนนำเข้า - ถ้ายิงหลาย chunk พร้อมกัน chunk ที่เจอรหัสใหม่ตัวเดียวกัน
    // จะสร้าง master ซ้ำกัน จึงต้องส่งทีละ chunk ให้ chunk ถัดไปเห็นของที่ chunk ก่อนสร้างไว้
    sequentialImport: true,
    templateAsset: partTemplateFile,
    templateFile: "09_Item_Master_Template.xlsx",
    exportFile: "ItemMaster_Export.xlsx",
    sheetName: "SAPUI5 Export", // ชื่อ sheet ของไฟล์ที่ SAP export ออกมา ห้ามเปลี่ยน
    headerRows: 1, // row 1 is the column header; data starts at row 2
    readByHeader: true, // จับคู่คอลัมน์จากชื่อหัวคอลัมน์ (ไฟล์ SAP เรียงคอลัมน์/ตั้งชื่อต่างจากเทมเพลต) ดู alignRowsByHeader
    columns: PART_COLUMNS,
    // All lookups are company-scoped and case-insensitive (ตรงกับที่ backend ใช้จับ master ตอนนำเข้า).
    lookups: {
      brand_code: { endpoint: "warehouse/brands", field: "brand_code", lower: true, flatten: (d) => (d || []).flatMap((b) => [b, ...(b.children || [])]) },
      model_code: { endpoint: "warehouse/models", field: "model_code", lower: true },
      uom_code: { endpoint: "warehouse/uoms", field: "uom_code", lower: true },
      serie_name: { endpoint: "warehouse/series", field: "name", lower: true },
    },
    // master ที่ระบบสร้างให้ระหว่างนำเข้า - แสดงจำนวนในสรุปผล เพื่อให้ admin ตามไปแก้ชื่อ/ตรวจต่อได้
    mastersSummary: [
      { key: "uoms", label: "import_part_uoms_created" },
      { key: "brands", label: "import_part_material_types_created" },
      { key: "models", label: "import_part_material_groups_created" },
      { key: "series", label: "import_part_brands_created" },
    ],
    toRow: (item) => [
      item.part_number || "",
      item.name || "",
      item.brand?.brand_code || "",
      item.model?.model_code || "",
      toSapUnit(item.uom),
      item.old_part_number || "",
      item.serie?.name || "",
    ],
    toPayload: (cells) => ({
      company_id: company?.id || "",
      part_number: cells.part_number.value,
      name: cells.name.value,
      brand_code: cells.brand_code?.value || "",
      model_code: cells.model_code?.value || "",
      // validateCell แยก "Kilogram (KG)" มาแล้ว: value = รหัส, name = ชื่อ (ใช้ตอนต้องสร้างหน่วยนับใหม่)
      uom_code: cells.uom_code.value,
      uom_name: cells.uom_code.name || "",
      old_part_number: cells.old_part_number?.value || "",
      serie_name: cells.serie_name?.value || "",
    }),
  },

  /**
   * โครงการ (Template No. 16) — ไฟล์ export รายการโครงการจาก SAP: 1 แถว = 1 โครงการ
   * upsert ด้วย project_code (มีแล้ว = update, ยังไม่มี = create) ส่วน WBS ของโครงการนำเข้าด้วยชุด "wbs"
   */
  project: {
    endpoint: "admin/projects",
    importPath: "admin/projects/import", // upsert by project_code
    // ประเภทงานไม่อยู่ในไฟล์ของ SAP และไฟล์หนึ่งชุดมักเป็นงานประเภทเดียวกันทั้งไฟล์
    // -> บังคับเลือกครั้งเดียวบนสุดของ modal ก่อนยืนยัน แล้วส่งที่ระดับ body ให้บันทึกกับทุกโครงการในไฟล์
    // ตัวเลือกดึงจาก master "ประเภทโครงการ" (เมนูตั้งค่าคำขอบริการ) เฉพาะที่เปิดใช้งาน - backend ตั้ง process ตามประเภทที่เลือก
    bulkOption: {
      bodyField: "project_type_id",
      label: "project_type",
      placeholder: "select_project_type",
      hint: "import_project_type_hint",
      endpoint: "admin/project-types",
      params: { is_active: "true" },
      valueField: "uid",
      // ชื่อประเภทตามภาษาที่ใช้อยู่ - แหล่งเดียวกับหน้าโครงการ (fetchBulkOptions เติม label ให้จาก toLabel)
      labelField: "label",
      toLabel: (o) => (localStorage.getItem("lang") === "EN" ? o.name_en || o.name_th : o.name_th) || "-",
    },
    // รหัสโครงการซ้ำในไฟล์ได้ (แถวหลังทับแถวก่อน) - ถ้ายิงหลาย chunk พร้อมกัน แถวซ้ำที่อยู่คนละ chunk
    // จะแย่งกันสร้างโครงการเดียวกัน (unique company_id + project_code) จึงต้องส่งทีละ chunk ตามลำดับไฟล์
    sequentialImport: true,
    templateAsset: projectTemplateFile,
    templateFile: "16_project_Template.xlsx",
    exportFile: "Project_Export.xlsx",
    sheetName: "SAPUI5 Export", // ชื่อ sheet ของไฟล์ที่ SAP export ออกมา ห้ามเปลี่ยน
    headerRows: 1, // row 1 is the column header; data starts at row 2
    columns: PROJECT_COLUMNS,
    readByHeader: true, // จับคู่คอลัมน์จากชื่อหัวคอลัมน์ ไม่ผูกกับตำแหน่ง (ดู alignRowsByHeader)
    toRow: (item) => [
      item.project_code || "",
      item.name || "",
      item.owner_code || "",
      item.owner_name || "",
      toSapDate(item.planned_start_date),
      toSapDate(item.planned_finish_date),
      toSapDate(item.actual_start_date),
      toSapDate(item.actual_finish_date),
      processingStatusExportLabel(item.processing_status),
      item.project_manager || "",
      item.sap_created_by || "",
      item.description || "",
      toSapDateTime(item.sap_created_on),
      item.cost_center || "",
      item.controlling_area || "",
      item.profit_center || "",
    ],
    toPayload: (cells) => ({
      company_id: company?.id || "",
      project_code: cells.project_code.value,
      name: cells.name.value,
      owner_code: cells.owner_code?.value || "",
      owner_name: cells.owner_name?.value || "",
      // validateCell แปลงเป็น dd/mm/yyyy (date) และ ISO UTC (datetime) มาแล้ว - backend รับได้ทั้งคู่
      planned_start_date: cells.planned_start_date?.value || "",
      planned_finish_date: cells.planned_finish_date?.value || "",
      actual_start_date: cells.actual_start_date?.value || "",
      actual_finish_date: cells.actual_finish_date?.value || "",
      processing_status: cells.processing_status?.value || "",
      project_manager: cells.project_manager?.value || "",
      sap_created_by: cells.sap_created_by?.value || "",
      description: cells.description?.value || "",
      sap_created_on: cells.sap_created_on?.value || "",
      // ช่องว่าง = ไม่ส่ง key -> backend ไม่ล้างค่าเดิม (ไฟล์เก่าที่ไม่มีคอลัมน์นี้ก็ไม่ทับ Costcenter ที่เคยนำเข้า)
      ...(costCenterOf(cells.cost_center?.value) ? { cost_center: costCenterOf(cells.cost_center.value) } : {}),
      ...(cells.controlling_area?.value ? { controlling_area: cells.controlling_area.value } : {}),
      ...(cells.profit_center?.value ? { profit_center: cells.profit_center.value } : {}),
    }),
  },

  wbs: {
    endpoint: "admin/wbs", // 1 แถว = 1 WBS (คืน project_code / name ของโครงการแบบแบนมาด้วย)
    importPath: "admin/wbs/import", // upsert project by project_code -> upsert wbs by wbs_element
    // แถวของโครงการเดียวกันกระจายอยู่ได้หลาย chunk - ถ้ายิงพร้อมกัน chunk ที่เจอโครงการใหม่ตัวเดียวกัน
    // จะสร้างโครงการซ้ำชนกัน (unique company_id + project_code) จึงต้องส่งทีละ chunk
    sequentialImport: true,
    // โครงการที่ "ไม่มีข้อมูลเจ้าของโครงการ" (ไม่พบในระบบ หรือพบแต่ owner_code/owner_name ว่าง) นำเข้า WBS ไม่ได้
    // -> key ไม่ตรงกับคอลัมน์ไหน validateCell จึงไม่ตีเป็น error แต่ excludeRows ตัดแถวนั้นออกจากรายการตอนตรวจสอบ
    // และนับจำนวนที่ตัดไปแสดงในข้อความสรุปตอนนำเข้า (excludedLabel) · ดึงรายการไม่สำเร็จ = ไม่ตัดอะไรออก
    lookups: {
      project_owner: {
        endpoint: "admin/projects",
        field: "project_code",
        filter: (p) => Boolean(String(p.owner_code ?? "").trim() || String(p.owner_name ?? "").trim()),
      },
    },
    excludeRows: (rows, lookupSets) => {
      const withOwner = lookupSets.project_owner;
      if (!withOwner) return { rows, excluded: 0 };
      const kept = rows.filter((r) => withOwner.has(String(r.cells.project_code?.value ?? "").trim()));
      return { rows: kept, excluded: rows.length - kept.length };
    },
    excludedLabel: "import_wbs_excluded_no_owner",
    templateAsset: wbsTemplateFile,
    templateFile: "13_wbs_Template.xlsx",
    exportFile: "WBS_Export.xlsx",
    sheetName: "project", // ชื่อ sheet ในไฟล์ template ห้ามเปลี่ยน
    headerRows: 1, // row 1 is the column header; data starts at row 2
    columns: WBS_COLUMNS,
    toRow: (item) => [
      item.entity_guid || "",
      item.project_code || "",
      item.name || "",
      item.wbs_element || "",
      item.wbs_element_name || "",
    ],
    toPayload: (cells) => ({
      company_id: company?.id || "",
      entity_guid: cells.entity_guid?.value || "",
      project_code: cells.project_code.value,
      name: cells.name.value,
      wbs_element: cells.wbs_element?.value || "",
      wbs_element_name: cells.wbs_element_name?.value || "",
    }),
  },

  /**
   * สต็อกคงเหลือ (Template No. 14) — ไฟล์ export จาก SAP: หัวคอลัมน์แถว 1 ข้อมูลเริ่มแถว 2
   * ต่างจากชุดอื่นตรงที่ไม่ได้ยิง rows เข้า endpoint เดียว แต่นำเข้าแบบ full-replace ผ่าน batch
   * (เปิดรอบ -> ส่งทีละ chunk -> commit) จึงใช้ parseFile/runImport ของ utils/stockImport แทน
   * pipeline กลาง ส่วนหน้าจอ preview/progress ใช้ร่วมกับชุดอื่นตามปกติ
   */
  stock: {
    endpoint: "warehouse/stocks/export", // คืนค่าเป็นแถวแบนรูปแบบเดียวกับไฟล์นำเข้า (1 แถว = 1 batch)
    importPath: "warehouse/stocks/import/batches",
    templateAsset: inventoryTemplateFile,
    templateFile: "14_Inventory_Template.xlsx",
    exportFile: "Inventory_Export.xlsx",
    sheetName: STOCK_IMPORT_SHEET,
    headerRows: STOCK_IMPORT_HEADER_ROWS,
    columns: STOCK_IMPORT_COLUMNS,
    // ไฟล์นี้ไม่มีคอลัมน์ UID ในเทมเพลต -> ส่งออกให้เหมือนเทมเพลตเป๊ะ ๆ เพื่อให้นำกลับเข้ามาได้ทันที
    noUidColumn: true,
    // ลบสต็อกเดิมทั้งบริษัทแล้วสร้างใหม่ -> ต้องเตือนให้ชัดกว่าชุดข้อมูลอื่น
    confirmDetail: "confirm_import_replace_all_detail",
    parseFile: async (file) => (await parseStockWorkbook(file)).rows,
    runImport: ({ rows, fileName, onProgress }) =>
      runStockImport({ rows, fileName, sheetName: STOCK_IMPORT_SHEET, onProgress }),
    summaryText: (summary) => stockImportSummaryText(summary),
    toRow: (item) => [
      item.material_code || "",
      item.material_name || "",
      item.plant_code || "",
      item.plant_name || "",
      item.storage_location_code || "",
      item.storage_location_name || "",
      item.special_stock_type || "",
      item.special_stock_type_name || "",
      item.wbs_element || "",
      item.wbs_element_name || "",
      item.batch_number || "",
      Number(item.unrestricted_qty || 0),
      Number(item.quality_inspection_qty || 0),
      Number(item.blocked_qty || 0),
    ],
  },

};

/**
 * Rows are POSTed to the import endpoint in chunks so the UI can show real
 * completion %. To keep it fast, chunks are sent with bounded concurrency
 * (IMPORT_CONCURRENCY at a time) instead of strictly one-after-another.
 * Each chunk is its own backend transaction; the whole-file client validation
 * (unique codes) keeps disjoint chunks conflict-free. Data sets with intra-file
 * dependencies set `sequentialImport: true` and provide `sortRowsForImport`
 * so the rows they depend on are always committed first.
 */
const IMPORT_CHUNK_SIZE = 100;
const IMPORT_CONCURRENCY = 4;

export default class ImportCenter extends Component {
  constructor(props) {
    super(props);
    this.fileInputRef = React.createRef();
    this.state = {
      filter_search: "",

      // Import preview modal
      import_modal: false,
      active_key: "",
      import_rows: [], // [{ cells: { key: { raw, value, error } }, valid }]
      import_file_name: "", // ชื่อไฟล์ที่เลือก (ชุดข้อมูลที่นำเข้าแบบ batch เก็บไว้ในรอบนำเข้า)
      importing: false, // true while import requests are in flight
      import_progress: 0, // 0-100 completion % across all chunks
      import_done: 0, // rows committed so far (for the "x / total" hint)
      show_invalid_only: false, // preview table: filter to error rows only
      import_excluded: 0, // แถวที่ถูกตัดออกตอนตรวจสอบ (cfg.excludeRows เช่น WBS ของโครงการที่ไม่มีเจ้าของโครงการ)

      // ตัวเลือกที่ต้องเลือกก่อนยืนยัน แล้วใช้กับทุกแถวในไฟล์ (cfg.bulkOption — ชุดข้อมูลช่าง = บทบาท, โครงการ = ประเภทงาน)
      bulk_options: [], // รายการตัวเลือกจาก cfg.bulkOption.options (คงที่) หรือดึงจาก cfg.bulkOption.endpoint
      bulk_value: "",   // ค่าที่ผู้ใช้เลือก — ว่าง = ยังยืนยันนำเข้าไม่ได้

      // Data set catalog (UI list)
      data_sets: [
        { key: "customer", name: "import_customer", description: "import_customer_desc", template: "01_Customer_Template.xlsx", icon: "", color: "primary" },
        { key: "technician", name: "import_technician", description: "import_technician_desc", template: "15_Technician_Template.xlsx", icon: "", color: "primary" },
        { key: "part_master", name: "import_part_master", description: "import_part_master_desc", template: "09_Item_Master_Template.xlsx", icon: "", color: "primary" },
        { key: "asset", name: "import_asset", description: "import_asset_desc", template: "10_Asset_Template.xlsx", icon: "", color: "primary" },
        { key: "warehouse", name: "import_warehouse", description: "import_warehouse_desc", template: "11_Warehouse_Template.xlsx", icon: "", color: "primary" },
        { key: "location", name: "import_location", description: "import_location_desc", template: "12_Location_Template.xlsx", icon: "", color: "primary" },
        { key: "uom", name: "import_uom", description: "import_uom_desc", template: "07_UnitOfMeasure_Template.xlsx", icon: "", color: "primary" },
        { key: "category", name: "import_category", description: "import_category_desc", template: "08_Category_Template.xlsx", icon: "", color: "primary" },
        // key ของ dataset/ชื่อไฟล์ template คงเดิม — เปลี่ยนเฉพาะชื่อที่แสดงผล (ดู glossary ใน components/global.js)
        { key: "brand", name: "import_material_type", description: "import_material_type_desc", template: "04_Material_Type_Template.xlsx", icon: "", color: "primary" },
        { key: "model", name: "import_material_group", description: "import_material_group_desc", template: "05.1_Material_Group_Template.xlsx", icon: "", color: "primary" },
        { key: "series", name: "import_brand", description: "import_brand_desc", template: "05.2_Brand_Template.xlsx", icon: "", color: "primary" },
        { key: "project", name: "import_project", description: "import_project_desc", template: "16_project_Template.xlsx", icon: "", color: "primary" },
        { key: "wbs", name: "import_wbs", description: "import_wbs_desc", template: "13_wbs_Template.xlsx", icon: "", color: "primary" },
        { key: "stock", name: "import_stock", description: "import_stock_desc", template: "14_Inventory_Template.xlsx", icon: "", color: "primary" },
      ],
    };
  }

  async componentDidMount() {
    loading(false);
  }

  // ---- Helpers -------------------------------------------------------------

  // Clear any leftover import preview / state. Called at the start of every action.
  resetImport = () => {
    this.setState({ import_modal: false, active_key: "", import_rows: [], import_file_name: "", importing: false, import_progress: 0, import_done: 0, show_invalid_only: false, import_excluded: 0, bulk_options: [], bulk_value: "" });
  };

  // Header row of a workbook for this data set (0-based row cfg.headerRows - 1).
  headerRowOf = (wb, cfg) => {
    const ws = wb.Sheets[cfg.sheetName] || wb.Sheets[wb.SheetNames[0]];
    if (!ws) return [];
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "", blankrows: true });
    return rows[cfg.headerRows - 1] || [];
  };

  /**
   * จัดคอลัมน์ของแถวข้อมูลใหม่ให้เรียงตาม cfg.columns โดยจับคู่จากชื่อหัวคอลัมน์ (en / th / aliases)
   * ไม่ผูกกับตำแหน่ง · คอลัมน์เกินในไฟล์ (เช่น UID ท้ายไฟล์ส่งออก) ถูกข้าม · หัวที่ไม่พบ = ค่าว่าง
   * @returns {{ rows: any[][], missing: string[] }} missing = ชื่อคอลัมน์บังคับที่หาไม่เจอในไฟล์
   */
  alignRowsByHeader = (cfg, wb, raw) => {
    const { colIndex, missing } = matchColumnsByHeader(this.headerRowOf(wb, cfg), cfg.columns);
    const rows = raw.map((arr) => colIndex.map((i) => (i < 0 ? "" : arr[i])));
    return { rows, missing };
  };

  // Read the "Reference" sheet of the uploaded file -> { columnKey: [allowed labels] }
  parseReference = (wb, cfg) => {
    const refMap = {};
    if (!cfg.reference) return refMap;

    const sheetName = wb.SheetNames.find((n) => n.includes(cfg.reference.sheetMatch));
    if (!sheetName) return refMap;

    // Section titles always live in column A; the allowed value may live in a
    // later column (e.g. customer Reference has "No." in A and the value in B).
    const valueCol = cfg.reference.valueColumn || 0;
    const rows = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], { header: 1, defval: "" });
    let current = null;
    rows.forEach((row) => {
      const head = String(row[0] ?? "").trim();
      // A "N. Title" line (or one of our section titles) is a section boundary.
      const isSectionHeader = /^\d+\s*\./.test(head) || cfg.reference.sections.some((s) => head.includes(s.startsWith));
      if (isSectionHeader) {
        const sec = cfg.reference.sections.find((s) => head.includes(s.startsWith));
        current = sec ? sec.columnKey : null; // unknown section -> stop collecting
        if (current) refMap[current] = [];
        return;
      }
      if (!current) return;
      const val = String(row[valueCol] ?? "").trim();
      if (!val) return;
      // skip the "ค่า (Value)" column-header row
      if (val.includes("ค่า") || val.toLowerCase().includes("value")) return;
      refMap[current].push(val);
    });
    return refMap;
  };

  // Derive the stored value for an enum reference label.
  //   default      "โซน (Zone)" -> "zone"   (extract the english key in parentheses)
  //   mode "label" "BPSB"       -> "BPSB"   (keep the value verbatim, e.g. a branch code)
  enumKeyOf = (col, label) => {
    if (col && col.enumKeyMode === "label") return String(label ?? "").trim();
    const m = String(label ?? "").match(/\(([^)]+)\)/);
    return (m ? m[1] : String(label ?? "")).trim().toLowerCase();
  };

  // Candidate {key,label} list for an enum column: prefer the Reference sheet, fall back to config options.
  enumCandidates = (col, refMap) => {
    const refValues = refMap && refMap[col.key];
    if (refValues && refValues.length) {
      return refValues.map((label) => ({ key: this.enumKeyOf(col, label), label }));
    }
    return (col.options || []).map((o) => ({ key: o, label: t(o) }));
  };

  // Match a cell value against the candidates by exact key or label (case-insensitive).
  matchEnum = (val, candidates) => {
    const v = String(val ?? "").trim().toLowerCase();
    if (!v) return null;
    for (const c of candidates) {
      const k = String(c.key).trim().toLowerCase();
      const l = String(c.label).trim().toLowerCase();
      if (v === k || v === l) return c.key;
    }
    return null;
  };

  normStatus = (val) => {
    const v = String(val ?? "").trim().toLowerCase();
    if (["เปิดใช้งาน", "ใช้งาน", "active", "true", "1"].includes(v)) return true;
    if (["ปิดใช้งาน", "ไม่ใช้งาน", "inactive", "false", "0"].includes(v)) return false;
    return null;
  };

  // Customer status is tri-state -> canonical "Active" | "Inactive" | "Hold" (null = invalid).
  normCustomerStatus = (val) => {
    const v = String(val ?? "").trim().toLowerCase();
    if (["active", "เปิดใช้งาน", "ใช้งาน", "true", "1"].includes(v)) return "Active";
    if (["inactive", "ปิดใช้งาน", "ไม่ใช้งาน", "false", "0"].includes(v)) return "Inactive";
    if (["hold", "on hold", "ระงับ", "พักใช้งาน", "พัก"].includes(v)) return "Hold";
    return null;
  };

  // Yes/No -> boolean. Empty defaults to false (No). Returns null for invalid values.
  normBool = (val) => {
    const v = String(val ?? "").trim().toLowerCase();
    if (v === "") return false;
    if (["yes", "y", "true", "1", "ใช่"].includes(v)) return true;
    if (["no", "n", "false", "0", "ไม่", "ไม่ใช่"].includes(v)) return false;
    return null;
  };

  // Basic email shape check (value is already trimmed by the caller).
  isEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(val ?? "").trim());

  // Excel เก็บวันที่เป็นเลขลำดับวัน (serial) นับจาก 30/12/1899 — ไฟล์ที่ export ออกมาจาก Excel
  // ส่งค่าพวกนี้มาเป็นตัวเลขล้วน ต้องแปลงเป็น dd/mm/yyyy ก่อน ไม่งั้นพรีวิวจะโชว์ "44525"
  // และหลังบ้านก็อ่านไม่ออก (1 = 31/12/1899, 2958465 = 31/12/9999)
  excelSerialToDate = (serial) => {
    const n = Number(serial);
    if (!Number.isFinite(n) || n < 1 || n > 2958465) return null;
    const ms = Date.UTC(1899, 11, 30) + Math.floor(n) * 86400000;
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return null;
    return `${String(d.getUTCDate()).padStart(2, "0")}/${String(d.getUTCMonth() + 1).padStart(2, "0")}/${d.getUTCFullYear()}`;
  };

  // Accept DD/MM/YYYY (incl. Buddhist year), an Excel date serial, or anything Date can parse;
  // returns the value to store (dd/mm/yyyy for serials, verbatim otherwise) or null when invalid.
  normDate = (val) => {
    let s = String(val ?? "").trim();
    if (!s) return null;
    // dd.mm.yyyy (รูปแบบวันที่ของ SAP) -> dd/mm/yyyy ต้องแปลงก่อนถึง new Date() ด้านล่าง
    // ไม่งั้น "01.09.2025" จะถูกอ่านแบบอเมริกันเป็น 9 ม.ค. แล้วผ่านการตรวจไปทั้งที่ผิดวัน
    const dotted = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (dotted) s = `${dotted[1].padStart(2, "0")}/${dotted[2].padStart(2, "0")}/${dotted[3]}`;
    const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (m) {
      const [, d, mo] = m;
      const day = Number(d), month = Number(mo);
      if (month < 1 || month > 12 || day < 1 || day > 31) return null;
      return s;
    }
    // yyyymmdd (SAP) - 8 หลักเสมอ จึงไม่ชนกับ Excel serial (สูงสุด 7 หลัก)
    const compact = s.match(/^(\d{4})(\d{2})(\d{2})$/);
    if (compact) {
      const [, y, mo, d] = compact;
      const dt = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)));
      if (dt.getUTCFullYear() !== Number(y) || dt.getUTCMonth() !== Number(mo) - 1 || dt.getUTCDate() !== Number(d)) return null;
      return `${d}/${mo}/${y}`;
    }
    if (/^\d+(\.\d+)?$/.test(s)) return this.excelSerialToDate(s);
    return Number.isNaN(new Date(s).getTime()) ? null : s;
  };

  /**
   * วันเวลา (UTC) -> { value: ISO string ที่ส่งเข้า backend, display: "dd.mm.yyyy, HH:MM:SS" } หรือ null ถ้าอ่านไม่ออก
   * รับ Excel serial (เศษทศนิยม = เวลา), "dd.mm.yyyy, HH:MM[:SS]" (คั่นวันที่ด้วย . / - ได้) และ ISO
   * ค่าในไฟล์ถือเป็น UTC ตามหัวคอลัมน์ "Created On (UTC)" จึงไม่แปลงตาม timezone ของเครื่องผู้ใช้
   */
  normDateTime = (val) => {
    const s = String(val ?? "").trim();
    if (!s) return null;
    const pad = (n) => String(n).padStart(2, "0");
    let ms = null;

    // yyyymmddHHMMSS (SAP, UTC) หรือ yyyymmdd (เที่ยงคืน UTC)
    const compact = s.match(/^(\d{4})(\d{2})(\d{2})(?:(\d{2})(\d{2})(\d{2}))?$/);
    if (compact) {
      const [y, mo, d, hh, mm, ss] = compact.slice(1).map((x) => Number(x || 0));
      if (mo < 1 || mo > 12 || d < 1 || d > 31 || hh > 23 || mm > 59 || ss > 59) return null;
      ms = Date.UTC(y, mo - 1, d, hh, mm, ss);
      if (new Date(ms).getUTCDate() !== d) return null;
    }

    const m = compact ? null : s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})(?:[,\sT]+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
    if (compact) {
      // handled above
    } else if (m) {
      let year = Number(m[3]);
      if (year > 2400) year -= 543; // พ.ศ. -> ค.ศ.
      const [day, month, hh, mm, ss] = [Number(m[1]), Number(m[2]), Number(m[4] || 0), Number(m[5] || 0), Number(m[6] || 0)];
      if (month < 1 || month > 12 || day < 1 || day > 31 || hh > 23 || mm > 59 || ss > 59) return null;
      ms = Date.UTC(year, month - 1, day, hh, mm, ss);
      if (new Date(ms).getUTCDate() !== day) return null; // กันวันที่ไม่มีจริง เช่น 31/02
    } else if (/^\d+(\.\d+)?$/.test(s)) {
      const n = Number(s);
      if (n < 1 || n > 2958465) return null;
      ms = Date.UTC(1899, 11, 30) + Math.round(n * 86400000);
    } else if (/^\d{4}-\d{1,2}-\d{1,2}/.test(s)) {
      const hasZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(s);
      const iso = s.includes("T") || s.includes(" ") ? s.replace(" ", "T") : `${s}T00:00:00`;
      ms = new Date(hasZone ? iso : `${iso}Z`).getTime();
    }

    if (ms === null || Number.isNaN(ms)) return null;
    const d = new Date(ms);
    return {
      value: d.toISOString(),
      display: `${pad(d.getUTCDate())}.${pad(d.getUTCMonth() + 1)}.${d.getUTCFullYear()}, ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`,
    };
  };

  validateCell = (col, raw, seen, refMap = {}, lookupSets = {}, lookupLower = {}) => {
    const s = raw === undefined || raw === null ? "" : String(raw).trim();
    // ค่าที่เอาไปเทียบกับ lookup set ต้องแปลงเคสให้ตรงกับตอนสร้าง set
    const lookupKey = (v) => (lookupLower[col.key] ? String(v).toLowerCase() : String(v));
    let value = s;
    let error = "";
    let detail = "";
    let note = ""; // หมายเหตุที่ไม่ใช่ error เช่น ค่าที่ยังไม่มีใน master แต่ระบบจะสร้างให้ (col.newIfMissing)
    let extra = {}; // ข้อมูลประกอบของ cell ที่ toPayload ใช้ต่อ (เช่น ชื่อหน่วยนับที่แยกจากรหัส)
    let display; // ค่าที่ให้พรีวิวแสดงแทน raw (ปล่อย undefined = โชว์ raw ตามเดิม)

    if (col.type === "number") {
      if (s !== "" && !isFinite(Number(s))) error = "number";
      else if (s !== "" && col.min !== undefined && Number(s) < col.min) error = "number";
    } else if (col.type === "email") {
      if (s === "") {
        if (col.required) error = "required";
      } else if (!this.isEmail(s)) {
        error = "email";
      }
    } else if (col.type === "date") {
      if (s === "") {
        if (col.required) error = "required";
      } else {
        const d = this.normDate(s);
        // serial ของ Excel ถูกแปลงเป็น dd/mm/yyyy แล้ว -> ให้ทั้งพรีวิวและ payload เห็นค่าที่อ่านออก
        if (d === null) error = "date";
        else { value = d; display = d; }
      }
    } else if (col.type === "datetime") {
      if (s === "") {
        if (col.required) error = "required";
      } else {
        const d = this.normDateTime(s);
        if (d === null) error = "date";
        else { value = d.value; display = d.display; }
      }
    } else if (col.type === "enum") {
      if (s === "") {
        if (col.required) error = "required";
      } else {
        const n = this.matchEnum(s, this.enumCandidates(col, refMap));
        if (!n) error = "enum";
        else value = n;
      }
    } else if (col.type === "branch") {
      // ชื่อสาขาเทียบกับ master สาขา ไม่สนตัวพิมพ์/ช่องว่างหน้า-หลัง; value = ชื่อตามระบบ
      if (s === "") {
        if (col.required) error = "required";
      } else {
        const branchMap = lookupSets[col.key];
        const found = branchMap && branchMap.get(s.toLowerCase());
        if (branchMap && !found) {
          error = "branch_not_found";
          detail = s;
        } else if (found && !found.is_active) {
          error = "branch_inactive";
          detail = s;
        } else if (found) {
          value = found.name;
        }
      }
    } else if (col.type === "status") {
      if (s === "") {
        if (col.required) error = "required";
      } else {
        const b = this.normStatus(s);
        if (b === null) error = "status";
        else value = b;
      }
    } else if (col.type === "customer_status") {
      if (s === "") {
        if (col.required) error = "required";
        else value = "Active"; // default missing status to Active
      } else {
        const cs = this.normCustomerStatus(s);
        if (cs === null) error = "customer_status";
        else value = cs; // "Active" | "Inactive" | "Hold"
      }
    } else if (col.type === "bool") {
      // Yes/No flag, optional, defaults to No.
      const b = this.normBool(s);
      if (b === null) error = "bool";
      else value = b;
    } else if (col.type === "list") {
      // Comma-separated codes; optionally checked against a lookup set (e.g. company locations).
      if (s === "") {
        if (col.required) error = "required";
        value = [];
      } else {
        const items = s.split(",").map((x) => x.trim()).filter(Boolean);
        value = items;
        const set = lookupSets[col.key];
        if (set) {
          const missing = items.filter((it) => !set.has(lookupKey(it)));
          if (missing.length) {
            error = "lookup";
            detail = missing.join(", ");
          }
        }
      }
    } else if (col.type === "sap_unit") {
      // หน่วยนับของ SAP "ชื่อ (รหัส)" -> value = รหัส (ใช้จับกับ master) ส่วนชื่อเก็บไว้ใน cell.name
      if (s === "") {
        if (col.required) error = "required";
      } else {
        const unit = parseSapUnit(s);
        if (!unit.code) {
          error = "sap_unit"; // มีวงเล็บแต่ไม่มีรหัสข้างใน เช่น "Kilogram ()"
        } else if (unit.code.length > SAP_UNIT_MAX_LENGTH || unit.name.length > SAP_UNIT_MAX_LENGTH) {
          error = "max_length";
          detail = String(SAP_UNIT_MAX_LENGTH);
        } else {
          value = unit.code;
          extra = { name: unit.name };
        }
      }
    } else {
      // text — optionally length-limited and checked against a single-value lookup set (e.g. brand_code).
      if (col.required && s === "") {
        error = "required";
      } else if (col.maxLength && s.length > col.maxLength) {
        error = "max_length";
        detail = String(col.maxLength);
      }
    }

    // ค่าที่ต้องมีอยู่ใน master (text / sap_unit): ไม่เจอ = error เว้นแต่คอลัมน์ประกาศ newIfMissing
    // ซึ่งแปลว่า backend จะสร้าง master ให้ตอนนำเข้า -> แค่ขึ้นหมายเหตุให้ผู้ใช้รู้ก่อนกดยืนยัน
    if (
      !error &&
      value !== "" &&
      (col.type === "text" || col.type === "sap_unit") &&
      lookupSets[col.key] &&
      !lookupSets[col.key].has(lookupKey(value))
    ) {
      if (col.newIfMissing) {
        note = col.newIfMissing;
      } else {
        error = "lookup";
        detail = s;
      }
    }

    if (!error && col.unique && s !== "") {
      const k = s.toLowerCase();
      if (seen[k]) error = "duplicate";
      else seen[k] = true;
    }

    return { raw: s, value, error, detail, ...extra, ...(note && { note }), ...(display !== undefined && { display }) };
  };

  validateRows = (cfg, raw, refMap = {}, lookupSets = {}, lookupLower = {}) => {
    const seen = {};
    const out = [];
    raw.forEach((arr) => {
      // Drop rows with no real data. Ignore the auto running-number column and
      // bool columns left at their default "No" (templates pre-fill these).
      const allEmpty = cfg.columns.every((c, ci) => {
        if (c.type === "auto") return true;
        const v = String(arr[ci] ?? "").trim();
        if (v === "") return true;
        if (c.type === "bool" && this.normBool(v) === false) return true;
        if (c.type === "number" && Number(v) === 0) return true; // templates pre-fill 0
        return false;
      });
      if (allEmpty) return;

      const cells = {};
      let valid = true;
      cfg.columns.forEach((c, ci) => {
        if (c.type === "auto") return;
        const res = this.validateCell(c, arr[ci], seen, refMap, lookupSets, lookupLower);
        cells[c.key] = res;
        if (res.error) valid = false;
      });
      out.push({ cells, valid });
    });
    return out;
  };

  // ข้อความ error ใช้ชุดเดียวกับหน้าตรวจข้อมูล (components/ImportPreviewModal.js)
  errorText = importErrorText;

  // Fetch the company-scoped lookups declared in cfg.lookups / cfg.exportLookups ->
  //   { sets: { columnKey: Set(codes) }, maps: { columnKey: Map(code -> mapField) },
  //     lower: { columnKey: true } }.
  // `maps` is only built for lookups that declare a `mapField` (e.g. uid -> model_code on export).
  // `lower` travels with the set so validateCell knows whether to compare case-insensitively —
  // it must match how the backend resolves the same value, otherwise the preview rejects rows
  // the import would have accepted (or the other way round).
  fetchLookupSets = async (lookups) => {
    const sets = {};
    const maps = {};
    const lower = {};
    if (!lookups) return { sets, maps, lower };
    const cache = {}; // endpoint -> data[] | null (so categories aren't fetched 3x)
    for (const [colKey, lk] of Object.entries(lookups)) {
      if (!(lk.endpoint in cache)) {
        const res = await GET(lk.endpoint, { page_size: 100000, page: 0 });
        cache[lk.endpoint] = res?.status ? res.data || [] : null;
      }
      const raw = cache[lk.endpoint];
      if (raw == null) continue; // fetch failed -> skip (avoids false errors)
      const flat = lk.flatten ? lk.flatten(raw) : raw; // e.g. unfold nested Material Type tree
      const data = lk.filter ? flat.filter(lk.filter) : flat;
      if (lk.statusField) {
        // Map(lower(trim(name)) -> {name, is_active}) — Map.has ใช้แทน Set ได้
        const byName = new Map();
        data.forEach((d) => {
          const name = String(d[lk.field] ?? "").trim();
          if (name) byName.set(name.toLowerCase(), { name, is_active: d[lk.statusField] !== false });
        });
        sets[colKey] = byName;
        lower[colKey] = true;
        continue;
      }
      const values = data.map((d) => String(d[lk.field] ?? "").trim()).filter(Boolean);
      // `lower` -> case-insensitive; default exact (codes / identifiers).
      sets[colKey] = new Set(lk.lower ? values.map((v) => v.toLowerCase()) : values);
      if (lk.lower) lower[colKey] = true;
      if (lk.mapField) {
        const m = new Map();
        data.forEach((d) => {
          const key = String(d[lk.field] ?? "").trim();
          if (key) m.set(lk.lower ? key.toLowerCase() : key, String(d[lk.mapField] ?? "").trim());
        });
        maps[colKey] = m;
      }
    }
    return { sets, maps, lower };
  };

  // Options for cfg.bulkOption — either a fixed list (`options`, label = i18n key, e.g. project type)
  // or company-scoped data fetched from `endpoint` (e.g. the roles a technician can be given).
  // A failed fetch returns [] -> the select stays empty and the confirm button stays disabled.
  fetchBulkOptions = async (cfg) => {
    if (!cfg.bulkOption) return [];
    if (Array.isArray(cfg.bulkOption.options)) {
      return cfg.bulkOption.options.map((o) => ({ ...o, label: t(o.label) }));
    }
    const res = await GET(cfg.bulkOption.endpoint, { page_size: 100000, page: 0, ...(cfg.bulkOption.params || {}) });
    const rows = res?.status ? res.data || [] : [];
    // toLabel (ถ้ามี) = ป้ายที่คำนวณจากหลายฟิลด์ เช่น ชื่อประเภทโครงการตามภาษา
    return cfg.bulkOption.toLabel ? rows.map((o) => ({ ...o, label: cfg.bulkOption.toLabel(o) })) : rows;
  };

  // ---- Actions -------------------------------------------------------------

  handleDownloadTemplate = async (item) => {
    this.resetImport();
    const cfg = DATASET_CONFIG[item.key];
    if (!cfg) {
      alert("", "info", t("import_center"), t("import_not_available"));
      return;
    }
    loading(true);
    try {
      const res = await fetch(cfg.templateAsset);
      const blob = await res.blob();
      saveAs(blob, cfg.templateFile);
    } catch (e) {
      alert("", "warning", t("warning"), t("download_failed"));
    }
    loading(false);
  };

  handleExport = async (item) => {
    this.resetImport();
    const cfg = DATASET_CONFIG[item.key];
    if (!cfg) {
      alert("", "info", t("import_center"), t("import_not_available"));
      return;
    }

    loading(true);
    const result = await GET(cfg.endpoint, { page_size: 100000, page: 0 });
    const data = result?.status ? result.data || [] : [];

    if (!data.length) {
      loading(false);
      alert("", "info", t("import_center"), t("export_no_data"));
      return;
    }

    try {
      // Build the export on top of the template so the layout matches the template.
      const res = await fetch(cfg.templateAsset);
      const ab = await res.arrayBuffer();
      const wb = XLSX.read(ab, { type: "array" });
      const ws = wb.Sheets[cfg.sheetName] || wb.Sheets[wb.SheetNames[0]];

      // Some data sets are nested (e.g. Material Types with children) -> flatten for a tabular export.
      const exportData = cfg.flattenForExport ? cfg.flattenForExport(data) : data;

      // Export-only lookups turn stored ids back into the codes the template expects
      // (e.g. technician skills store a Material Group uid -> written out as its code).
      const { maps: exportMaps } = await this.fetchLookupSets(cfg.exportLookups);

      const headerRowIdx = cfg.headerRows - 1; // 0-based row of the column headers
      const firstDataRow = cfg.headerRows;     // 0-based row of the first data row
      // UID is appended right after the last template column, except for data sets whose
      // file must stay byte-for-byte the template layout (`noUidColumn`).
      const withUid = !cfg.noUidColumn;
      const uidCol = cfg.columns.length;
      const range = XLSX.utils.decode_range(ws["!ref"]);
      const lastCol = withUid ? Math.max(range.e.c, uidCol) : Math.max(range.e.c, cfg.columns.length - 1);

      // 1) Clear the data area. Templates ship with placeholder rows (running numbers,
      //    "No" defaults, zeros) that would otherwise leak below the real data.
      for (let r = firstDataRow; r <= range.e.r; r++) {
        for (let c = 0; c <= lastCol; c++) {
          delete ws[XLSX.utils.encode_cell({ r, c })];
        }
      }

      // 2) Append a UID column header at the end of the header row.
      if (withUid) XLSX.utils.sheet_add_aoa(ws, [["UID"]], { origin: { r: headerRowIdx, c: uidCol } });

      // 3) Write the real company data; each row carries its own uid at the very end.
      exportData.forEach((row, i) => {
        const arr = cfg.toRow(row, i, { maps: exportMaps });
        if (withUid) arr[uidCol] = row.uid || ""; // uid lands in the column after the last template column
        XLSX.utils.sheet_add_aoa(ws, [arr], { origin: { r: firstDataRow + i, c: 0 } });
      });

      // 4) Grow the sheet range to cover the written data + the UID column.
      range.e.c = lastCol;
      range.e.r = Math.max(headerRowIdx, firstDataRow + exportData.length - 1);
      ws["!ref"] = XLSX.utils.encode_range(range);

      const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(new Blob([out], { type: "application/octet-stream" }), cfg.exportFile);
    } catch (e) {
      alert("", "warning", t("warning"), t("download_failed"));
    }
    loading(false);
  };

  handleImport = (item) => {
    this.resetImport();
    const cfg = DATASET_CONFIG[item.key];
    if (!cfg) {
      alert("", "info", t("import_center"), t("import_not_available"));
      return;
    }
    this.setState({ active_key: item.key }, () => {
      if (this.fileInputRef.current) this.fileInputRef.current.click();
    });
  };

  handleFileSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;

    const cfg = DATASET_CONFIG[this.state.active_key];
    if (!cfg) return;

    loading(true);
    try {
      let rows;
      let excluded = 0; // แถวที่ cfg.excludeRows ตัดออก (ไม่ใช่ error - แค่ไม่เอาเข้ารายการ)
      if (cfg.parseFile) {
        // ชุดข้อมูลที่มีตัวอ่าน/ตรวจไฟล์ของตัวเอง (สต็อก) — คืนค่ารูปแบบเดียวกับ validateRows
        rows = await cfg.parseFile(file);
      } else {
        const ab = await file.arrayBuffer();
        const wb = XLSX.read(ab, { type: "array" });
        const ws = wb.Sheets[cfg.sheetName] || wb.Sheets[wb.SheetNames[0]];

        // อ่านตามชื่อหัวคอลัมน์ (ไม่ผูกตำแหน่ง) ตรวจแค่ว่ามีคอลัมน์บังคับครบ
        let raw = XLSX.utils.sheet_to_json(ws, {
          header: 1,
          range: cfg.headerRows, // start reading at the first data row
          defval: "",
        });
        const aligned = this.alignRowsByHeader(cfg, wb, raw);
        if (aligned.missing.length) {
          loading(false);
          alert("", "warning", t("warning"), `${t("import_required_column_missing")}: ${aligned.missing.join(", ")}`);
          return;
        }
        raw = aligned.rows;
        const refMap = this.parseReference(wb, cfg);
        const { sets: lookupSets, maps: lookupMaps, lower: lookupLower } = await this.fetchLookupSets(cfg.lookups);
        rows = this.validateRows(cfg, raw, refMap, lookupSets, lookupLower);
        if (cfg.postValidate) rows = cfg.postValidate(rows, lookupSets);
        if (cfg.excludeRows) ({ rows, excluded } = cfg.excludeRows(rows, lookupSets));
        if (cfg.enrichRows) rows = cfg.enrichRows(rows, { maps: lookupMaps });
      }

      // ตัวเลือกที่ต้องเลือกก่อนยืนยัน (เช่น บทบาทของช่าง) — ดึงมาพร้อมพรีวิว
      const bulkOptions = await this.fetchBulkOptions(cfg);

      if (!rows.length) {
        loading(false);
        // ถูกตัดออกหมดทั้งไฟล์ -> บอกเหตุผลแทน "ไม่พบข้อมูล"
        const allExcluded = excluded > 0 && cfg.excludedLabel;
        alert("", "info", t("import_center"), allExcluded ? `${t(cfg.excludedLabel)}: ${formatNumber(excluded)}` : t("no_rows_found"));
        return;
      }
      // Keep the overlay up until the modal has actually rendered, otherwise the
      // loader closes a frame before the modal appears and the screen flickers.
      this.setState(
        {
          import_modal: true,
          import_rows: rows,
          import_file_name: file.name || "",
          show_invalid_only: false,
          import_excluded: excluded,
          bulk_options: bulkOptions,
          bulk_value: "",
        },
        () => loading(false)
      );
    } catch (err) {
      loading(false);
      const missingColumns = err && err.missingColumns;
      alert("", "warning", t("warning"), missingColumns ? `${t("import_required_column_missing")}: ${missingColumns.join(", ")}` : t("import_read_failed"));
    }
  };

  /**
   * นำเข้าด้วยขั้นตอนเฉพาะของชุดข้อมูล (ตอนนี้มีแค่สต็อก)
   * ตัวนำเข้าเป็นคนคุมลำดับ/ความปลอดภัยของข้อมูลเอง หน้านี้ทำแค่ยืนยัน แสดง progress และสรุปผล
   */
  runCustomImport = async (cfg, rows, fileName) => {
    if (
      !(await alert("", "warning", t("confirm"), t(cfg.confirmDetail || "confirm_import_detail"), t("confirm"), t("cancel")))
    ) {
      return;
    }

    this.setState({ importing: true, import_progress: 0, import_done: 0 });

    const result = await cfg.runImport({
      rows,
      fileName,
      onProgress: (percent, done) => this.setState({ import_progress: percent, import_done: done }),
    });

    if (!result?.ok) {
      this.setState({ importing: false, import_progress: 0, import_done: 0 });
      const detail = result?.errors?.length ? `\n${result.errors.slice(0, 10).join("\n")}` : "";
      alert("", "warning", t("warning"), `${result?.message || t("import_failed")}${detail}`);
      return;
    }

    this.setState({ importing: false });
    this.resetImport();
    alert(
      "",
      "success",
      t("import_done"),
      cfg.summaryText ? cfg.summaryText(result.summary || {}) : t("import_done")
    );
  };

  handleConfirmImport = async () => {
    const { active_key, import_rows, import_file_name } = this.state;
    const cfg = DATASET_CONFIG[active_key];
    if (!cfg) return;
    if (!import_rows.length || import_rows.some((r) => !r.valid)) return;
    // ตัวเลือกที่ใช้กับทุกแถว (เช่น บทบาท) ต้องถูกเลือกก่อน ปุ่มยืนยันถูก disable ไว้อยู่แล้ว
    // แต่กันไว้อีกชั้นเผื่อถูกเรียกจากทางอื่น
    if (cfg.bulkOption && !this.state.bulk_value) return;

    // ชุดข้อมูลที่มีขั้นตอนนำเข้าเฉพาะของตัวเอง (สต็อก = full-replace ผ่าน batch)
    if (cfg.runImport) {
      await this.runCustomImport(cfg, import_rows, import_file_name);
      return;
    }

    // No bulk upload endpoint yet for this data set -> validated/previewed only.
    if (!cfg.importPath) {
      alert("", "info", t("import_center"), t("import_pending_backend"));
      return;
    }

    if (
      !(await alert("", "info", t("confirm"), t("confirm_import_detail"), t("confirm"), t("cancel")))
    ) {
      return;
    }

    // Strip per-row company_id — it is sent once at the top level of the body.
    let rows = import_rows.map((r) => {
      const { company_id, ...rest } = cfg.toPayload(r.cells); // eslint-disable-line no-unused-vars
      return rest;
    });
    // Order rows where chunk boundaries matter (rows a later row depends on go first).
    if (cfg.sortRowsForImport) rows = cfg.sortRowsForImport(rows);

    const total = rows.length;
    const chunks = [];
    for (let i = 0; i < total; i += IMPORT_CHUNK_SIZE) chunks.push(rows.slice(i, i + IMPORT_CHUNK_SIZE));

    // Drive an in-modal progress bar; the static-backdrop modal + disabled buttons
    // block every other action while it runs.
    this.setState({ importing: true, import_progress: 0, import_done: 0 });

    let created = 0;
    let updated = 0;
    const mastersCreated = {}; // master ที่ backend สร้างให้ระหว่างนำเข้า รวมทุก chunk (cfg.mastersSummary)
    let done = 0;
    let failMessage = "";
    let aborted = false;

    // ค่าที่เลือกไว้ใน modal ส่งไปครั้งเดียวต่อ chunk ที่ระดับ body ไม่ต้องยัดซ้ำทุกแถว
    const extraBody = cfg.bulkOption ? { [cfg.bulkOption.bodyField]: this.state.bulk_value } : {};

    const sendChunk = async (chunk) => {
      if (aborted) return;
      const res = await POST(cfg.importPath, { company_id: company?.id || "", ...extraBody, rows: chunk });
      if (!res?.status) {
        aborted = true; // stop sending further chunks once one fails
        if (!failMessage) failMessage = res?.message || t("import_failed");
        return;
      }
      const d = res.data || {};
      created += d.created ?? 0;
      updated += d.updated ?? 0;
      Object.entries(d.masters_created || {}).forEach(([key, count]) => {
        mastersCreated[key] = (mastersCreated[key] || 0) + (Number(count) || 0);
      });
      done = Math.min(done + chunk.length, total);
      this.setState({ import_done: done, import_progress: Math.round((done / total) * 100) });
    };

    // Worker pool: send up to `concurrency` chunks at once for speed. Data sets with
    // intra-file dependencies run one at a time to preserve the row order they need.
    const concurrency = cfg.sequentialImport ? 1 : Math.min(IMPORT_CONCURRENCY, chunks.length || 1);
    let next = 0;
    const worker = async () => {
      while (next < chunks.length && !aborted) {
        const myChunk = chunks[next++];
        await sendChunk(myChunk);
      }
    };
    await Promise.all(Array.from({ length: concurrency }, worker));

    this.setState({ importing: false });

    // จำนวน master ที่ถูกสร้างใหม่ (เฉพาะชนิดที่มีจริง) ต่อท้ายสรุปผล ทั้งตอนสำเร็จและตอนล้มกลางทาง
    const mastersText = (cfg.mastersSummary || [])
      .filter((m) => mastersCreated[m.key])
      .map((m) => `${t(m.label)}: ${formatNumber(mastersCreated[m.key])}`)
      .join("  |  ");
    const mastersLine = mastersText ? `\n${mastersText}` : "";
    // จำนวนแถวที่ถูกตัดออกตอนตรวจสอบ (เฉพาะชุดข้อมูลที่กำหนด excludedLabel เช่น WBS) ต่อท้ายสรุปผล
    const excludedLine =
      cfg.excludedLabel && this.state.import_excluded > 0
        ? `  |  ${t(cfg.excludedLabel)}: ${formatNumber(this.state.import_excluded)}`
        : "";

    if (!failMessage) {
      this.resetImport();
      alert(
        "",
        "success",
        t("import_done"),
        `${t("import_created_count")}: ${formatNumber(created)}  |  ${t("import_updated_count")}: ${formatNumber(updated)}${excludedLine}${mastersLine}`
      );
    } else {
      // Chunks commit independently -> some rows may already be in. Report the partial
      // result (re-importing is an idempotent upsert by code).
      this.setState({ import_progress: 0, import_done: 0 });
      const partial =
        created + updated > 0
          ? `\n${t("import_created_count")}: ${formatNumber(created)}  |  ${t("import_updated_count")}: ${formatNumber(updated)}${excludedLine}${mastersLine}`
          : "";
      alert("", "warning", t("warning"), failMessage + partial);
    }
  };

  // ---- Render --------------------------------------------------------------

  render() {
    const { filter_search, data_sets, import_modal, import_rows, active_key, importing, import_progress, import_done, show_invalid_only, import_excluded, bulk_options, bulk_value } = this.state;

    const keyword = filter_search.trim().toLowerCase();
    const filtered = keyword
      ? data_sets.filter(
          (d) =>
            t(d.name).toLowerCase().includes(keyword) ||
            t(d.description).toLowerCase().includes(keyword) ||
            d.template.toLowerCase().includes(keyword)
        )
      : data_sets;

    const cfg = DATASET_CONFIG[active_key];
    const activeName = data_sets.find((d) => d.key === active_key)?.name || "";

    return (
      <div className="w-100 min-vh-100" style={{ backgroundColor: "#F4F7FE" }}>
        <NavbarWarehouse />

        {/* Hidden file input for import */}
        <input
          ref={this.fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          className="d-none"
          onChange={this.handleFileSelected}
        />

        <div className="body p-4 mt-3">
          {/* Header Section */}
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
            <div>
              <h3 className="fw-bold mb-1 text-dark">{t("import_center")}</h3>
              <div className="text-secondary text-14">{t("import_center_desc")}</div>
            </div>
          </div>

          {/* Search Toolbar */}
          <div className="bg-white rounded-4 shadow-sm p-4 mb-4">
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <label className="fw-bold text-dark text-14 mb-0">{t("search")}:</label>
              <div className="position-relative" style={{ maxWidth: "360px", width: "100%" }}>
                <input
                  type="text"
                  className="form-control wpx-300"
                  style={{ height: "38px" }}
                  placeholder={t("type_to_search")}
                  value={filter_search}
                  onChange={(e) => this.setState({ filter_search: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Data Set Table */}
          <div className="bg-white rounded-4 shadow-sm mb-4 overflow-hidden">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0" style={{ minWidth: "900px" }}>
                <thead>
                  <tr className="text-white fw-bold text-12 bg-primary">
                    <th className="py-3 border-0 ps-4">{t("data_set")}</th>
                    <th className="py-3 border-0 text-center">{t("template_file")}</th>
                    <th className="py-3 border-0 text-center pe-4 wpx-450">{t("actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="text-center text-secondary py-5">
                        {t("no_data")}
                      </td>
                    </tr>
                  ) : (
                    filtered.map((item, idx) => (
                      <tr key={idx} className="border-bottom border-light bg-white">
                        {/* Data Set Info */}
                        <td className="py-3 ps-4">
                          <div className="d-flex align-items-center">
                            <div
                              className={`wpx-35 hpx-35 rounded text-white d-flex align-items-center justify-content-center me-3 flex-shrink-0 fw-bold text-14 bg-${item.color}`}
                            >
                              <span className="icon">{item.icon}</span>
                            </div>
                            <div>
                              <div className="fw-bold text-dark text-15">{t(item.name)}</div>
                              <div className="text-secondary text-12">{t(item.description)}</div>
                            </div>
                          </div>
                        </td>

                        {/* Template File */}
                        <td className="text-center">
                          <span
                            className="badge bg-primary-light text-primary rounded-pill px-3 py-2 fw-bold text-12 pointer"
                            onClick={() => this.handleDownloadTemplate(item)}
                          >
                            {item.template}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="text-center pe-4">
                          <div className="d-flex flex-column align-items-end gap-1">
                            <div className="d-flex justify-content-end gap-2">
                              <button
                                className="btn btn-white border px-3 py-2 fw-bold rounded-3 shadow-sm d-flex align-items-center justify-content-center gap-2 text-13 wpx-120"
                                onClick={() => this.handleDownloadTemplate(item)}
                              >
                                <span className="icon">{"\uf019"}</span> {t("template")}
                              </button>
                              <button
                                className="btn btn-white border px-3 py-2 fw-bold rounded-3 shadow-sm d-flex align-items-center justify-content-center gap-2 text-13 wpx-120"
                                onClick={() => this.handleExport(item)}
                              >
                                <span className="icon">{"\uf56e"}</span> {t("export")}
                              </button>
                              {canImport(item.key) && (
                                  <button
                                    className="btn btn-primary px-3 py-2 fw-bold rounded-3 shadow-sm d-flex align-items-center justify-content-center gap-2 text-13 wpx-120"
                                    onClick={() => this.handleImport(item)}
                                  >
                                    <span className="icon">{"\uf56f"}</span> {t("import")}
                                  </button>
                              )}
                            </div>
                            <div className="text-success text-11 fw-bold">
                              {DATASET_CONFIG[item.key] ? t("ready_for_import") : t("coming_soon")}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* IMPORT PREVIEW MODAL — คอมโพเนนต์เดียวกับปุ่มนำเข้าในเมนูสต็อกสินค้า */}
        <ImportPreviewModal
          show={import_modal}
          title={t("import_preview_title")}
          subtitle={activeName ? t(activeName) : ""}
          warning={[
            cfg?.confirmDetail ? t(cfg.confirmDetail) : "",
            // แถวที่ถูกตัดออกตอนตรวจสอบ (เช่น WBS ของโครงการที่ไม่มีเจ้าของโครงการ) บอกไว้ก่อนยืนยัน
            cfg?.excludedLabel && import_excluded > 0 ? `${t(cfg.excludedLabel)}: ${formatNumber(import_excluded)}` : "",
          ]
            .filter(Boolean)
            .join(" · ")}
          columns={cfg ? cfg.columns : []}
          rows={import_rows}
          importing={importing}
          progress={import_progress}
          done={import_done}
          showInvalidOnly={show_invalid_only}
          onToggleInvalidOnly={(value) => this.setState({ show_invalid_only: value })}
          bulkOption={cfg?.bulkOption || null}
          bulkOptions={bulk_options}
          bulkValue={bulk_value}
          onBulkChange={(value) => this.setState({ bulk_value: value })}
          onCancel={this.resetImport}
          onConfirm={this.handleConfirmImport}
        />

      </div>
    );
  }
}
